import { useMemo } from 'react';
import { CardData, Position, ProcessedConnection } from '../types';
import { 
  getDiamondConnectionPoint, 
  getConnectorPath, 
  getReturnLoopPath, 
  groupConnectionsByTarget, 
  identifyTrunkPath, 
  findConvergencePoint, 
  truncatePathAtPoint, 
  parsePathToSegments,
  addLineJumps,
  detectIntersections,
  applyManualJumps
} from '../utils/vsmGeometry';
import { getUniqueConnections, findDuplicateConnections } from '../utils/edgeUtils';
import { 
  useVisibleNodes, 
  processConnectionsForExpandedGroups,
  ExpandedGroupInfo,
  EdgeRedirectMap
} from './useVisibleNodes';
import { buildChannelGrid, routeAllConnections } from '../utils/channelRouter';
import { isCancelledProgramInfoCard, shouldRemoveCancelledProgramInfoConnection } from '../utils/vsmCommunicationCleanup';

// Constante para separação entre setas múltiplas
const SEPARATION = 15;
// Constante para offset do tronco de retorno (distância da borda do card destino)
const RETURN_TRUNK_OFFSET = 40;

// 🛣️ CHANNEL ROUTER: Flag para ativar o novo sistema de roteamento por canais
// Quando true, usa o sistema de "rodovias" com canais pré-alocados
// Quando false, usa o sistema legado de obstacle avoidance (REGRAS TESTADAS do vsmGeometry.ts)
const USE_CHANNEL_ROUTER = false; // ✅ DESATIVADO: Restaurar regras corretas do vsmGeometry.ts

/**
 * Interface para conexão de retorno com informações de roteamento
 */
interface ReturnConnectionInfo {
  connectionId: string;
  sourceId: string;
  targetId: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourceHandle: string;
  color: string;
  isReturn: boolean;
  distanceFromTarget: number; // Distância horizontal do source até o target
}

interface UseEdgeCalculationsProps {
  activeCards: CardData[];
  highlightedPath: string[] | null;
  currentLayout: string;
  isTimeScaleActive?: boolean;
  /** Mapa de grupos expandidos: subProcessId -> cards filhos */
  expandedGroups?: Map<string, CardData[]>;
  /** V210: viewMode para alinhar âncoras de setas no modo Futuro (supermercado convertido centrado no bbox). */
  viewMode?: string;
}

export const useEdgeCalculations = ({
  activeCards,
  highlightedPath,
  currentLayout,
  isTimeScaleActive = false,
  expandedGroups = new Map(),
  viewMode
}: UseEdgeCalculationsProps) => {
  const isFutureMode = viewMode === 'future_simulated';
  
  // 🎯 FLATTENED VIEW: Obter nós visíveis considerando grupos expandidos
  const { 
    visibleNodes, 
    expandedGroupsInfo, 
    redirectMap,
    getEntryNodeId,
    getExitNodeId
  } = useVisibleNodes({ activeCards, expandedGroups });
  
  // ============================================================================
  // 🛣️ CHANNEL ROUTER: Sistema de roteamento por canais "rodovia"
  // Se ativado, substitui TODA a lógica legada de obstacle avoidance
  // ============================================================================
  const channelRouterResult = useMemo(() => {
    if (!USE_CHANNEL_ROUTER) return null;
    if (!visibleNodes || visibleNodes.length === 0) return { paths: [], mergeNodes: [] };
    
    // 1. Construir grid de canais baseado nas posições dos cards
    const grid = buildChannelGrid(visibleNodes, isTimeScaleActive);
    
    // 2. Rotear todas as conexões usando canais pré-alocados
    const routed = routeAllConnections(
      visibleNodes,
      grid,
      isTimeScaleActive,
      currentLayout,
      highlightedPath
    );
    
    return { paths: routed, mergeNodes: [] as Array<{ x: number; y: number; pathId: string; isMergeNode: boolean; targetId: string }> };
  }, [visibleNodes, isTimeScaleActive, currentLayout, highlightedPath]);

  const connectionPaths = useMemo(() => {
    // 🛣️ Se channel router está ativo, usar seus resultados diretamente
    if (USE_CHANNEL_ROUTER && channelRouterResult) {
      return channelRouterResult;
    }
    
    // Removido log excessivo para melhor performance
    const paths: ProcessedConnection[] = [];
    if (!visibleNodes || visibleNodes.length === 0) return { paths: [], mergeNodes: [] };

    const pathsWithoutJumps: ProcessedConnection[] = [];
    
    // GESTÃO DE PORTAS DE SAÍDA: Calcular offsets verticais para separar
    // saídas de Avanço (topo) das de Retorno (fundo)
    // GESTÃO DE PORTAS DE SAÍDA: Remover offsets - todas saem do centro
    // Isso garante que setas de avanço e retorno saiam do mesmo ponto
    const exitOffsets = new Map<string, number>(); // key: conexão ID, value: offsetY (sempre 0)

    visibleNodes.forEach(sourceCard => {
      if (!sourceCard.conexoes_saida || sourceCard.conexoes_saida.length === 0) return;
      
      // TODAS as conexões saem do CENTRO (offset 0)
      sourceCard.conexoes_saida.forEach((conn) => {
        exitOffsets.set(conn.id, 0); // Sem offset - todas saem do centro
      });
    });

    // GESTÃO DE PORTAS DE ENTRADA: Distribuir setas que chegam ao mesmo handle do mesmo card
    // → evita sobreposição quando 2+ setas convergem no mesmo ponto (ex.: PCPM + OXOX no topo de "Expedição")
    const ENTRY_SPACING = 30; // distância padrão entre setas que entram no mesmo lado
    const entryOffsets = new Map<string, number>(); // key: conn.id, value: offset perpendicular ao handle
    const entryHandleDir = new Map<string, 'top' | 'bottom' | 'left' | 'right'>(); // key: conn.id
    {
      type Inbound = { connId: string; sourceCard: CardData; targetCard: CardData; handle: 'top' | 'bottom' | 'left' | 'right' };
      const groups = new Map<string, Inbound[]>(); // key: `${targetId}:${handle}`
      const cardById = new Map(visibleNodes.map(n => [n.id, n] as const));
      visibleNodes.forEach(srcCard => {
        if (!srcCard.conexoes_saida) return;
        srcCard.conexoes_saida.forEach(conn => {
          if (!conn?.targetId) return;
          const tgtCard = cardById.get(conn.targetId);
          if (!tgtCard) return;
          const isKanbanInfo = conn.connectionType === 'vsm_info' && Boolean((conn as any).kanbanLoopEnabled);
          const resolvedHandle = (isKanbanInfo
            ? (isFutureMode ? 'top' : 'right')
            : (conn as any).targetHandle) as 'top' | 'bottom' | 'left' | 'right' | undefined;
          if (!resolvedHandle) return; // sem handle explícito → não distribui (mantém comportamento atual)
          const key = `${conn.targetId}:${resolvedHandle}`;
          const list = groups.get(key) ?? [];
          list.push({ connId: conn.id, sourceCard: srcCard, targetCard: tgtCard, handle: resolvedHandle });
          groups.set(key, list);
          entryHandleDir.set(conn.id, resolvedHandle);
        });
      });
      groups.forEach(list => {
        if (list.length < 2) return;
        // Ordenar pela posição do source perpendicular ao handle (X para top/bottom, Y para left/right)
        const axis: 'x' | 'y' = (list[0].handle === 'top' || list[0].handle === 'bottom') ? 'x' : 'y';
        list.sort((a, b) => (a.sourceCard.posicao?.[axis] ?? 0) - (b.sourceCard.posicao?.[axis] ?? 0));
        const n = list.length;
        list.forEach((item, i) => {
          entryOffsets.set(item.connId, (i - (n - 1) / 2) * ENTRY_SPACING);
        });
      });
    }

    // 🎯 EDGE RE-ROUTING: Iterar sobre nós visíveis e aplicar redirecionamentos
    visibleNodes.forEach(s => {
      if (!s || !s.conexoes_saida || s.conexoes_saida.length === 0) return;
      
      // Verificar se este card é o último de um grupo expandido
      // Se for, ele herda as conexões de saída do grupo pai
      const parentGroupId = redirectMap.childToGroup.get(s.id);
      let connectionsToProcess = s.conexoes_saida;
      
      // Se é filho de um grupo expandido, verificar se é o exitCard
      if (parentGroupId) {
        const groupInfo = expandedGroupsInfo.get(parentGroupId);
        if (groupInfo && groupInfo.exitCard.id === s.id) {
          // Este é o último card do grupo - herdar conexões de saída do grupo pai
          const parentCard = activeCards.find(c => c.id === parentGroupId);
          if (parentCard?.conexoes_saida && parentCard.conexoes_saida.length > 0) {
            connectionsToProcess = parentCard.conexoes_saida;
          }
        } else {
          // Não é o último card - usar apenas conexões internas
          // (conexões para outros cards do mesmo grupo ou para cards externos)
        }
      }
      
      connectionsToProcess.forEach(c => {
        if (!c || !c.targetId) return;
        if (isCancelledProgramInfoCard(s) || shouldRemoveCancelledProgramInfoConnection(c, activeCards.find(x => x && x.id === c.targetId))) return;
        
        // 🎯 REDIRECIONAR TARGET: Se o target é um grupo expandido, ir para seu entryCard
        const effectiveTargetId = getEntryNodeId(c.targetId);
        
        // Buscar o card de destino nos nós visíveis
        let t = visibleNodes.find(x => x && x.id === effectiveTargetId);
        
        // Se não encontrou, tentar buscar no activeCards original (pode ser grupo minimizado)
        if (!t) {
          t = activeCards.find(x => x && x.id === c.targetId);
        }
        
        if(!t || !t.posicao) return;
        
        const sIsClassic = s.layoutType === 'classic';
        const tIsClassic = t.layoutType === 'classic';
        const sourcePos = s.posicao;
        const targetPos = t.posicao;
        const isSourceDiamond = s.tipo === 'decisao';
        const isTargetDiamond = t.tipo === 'decisao';
        
        // Mapa de tamanhos reais dos cards de inventário (VSM factory)
        const INV_W_MAP: Record<string, number> = { fifo: 240, puxado: 102, nivelamento: 192, entrega: 114, estoque: 104, pulmao: 65, supermercado: 66, vaver: 104 };
        const INV_H_MAP: Record<string, number> = { fifo: 80,  puxado: 77,  nivelamento: 144, entrega: 86,  estoque: 80,  pulmao: 110, supermercado: 110, vaver: 80 };

        const VSM_ENTITY_PROCESS_WIDTH = 127;
        const VSM_PROCESS_DATABOX_WIDTH = 121;
        const VSM_PROCESS_HEIGHT = 90;
        const VSM_PROCESS_DATABOX_BODY_RATIO = 104 / 207;
        const VSM_ENTITY_VISUAL_CENTER_NUDGE_X = 6;
        const getProcessDataBoxHeight = () => Math.round(VSM_PROCESS_HEIGHT / VSM_PROCESS_DATABOX_BODY_RATIO);
        const hasDataBoxField = (card: CardData) => !!((card as any).vsmCT || (card as any).vsmCO || (card as any).vsmDisp);
        const isProcessoCard = (card: CardData) => {
          const icon = String((card as any).vsmIcon ?? '');
          const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
          return card.tipo === 'atividade' && (icon === 'processo' || /solda\s*(i|ii)\b|estamparia|montagem|expedi[cç][aã]o/i.test(label));
        };
        const isExpeditionCard = (card: CardData) =>
          (card as any).vsmSubtype === 'expedition' || /expedi/i.test(String((card as any).atividade ?? ''));
        const isPcpmCard = (card: CardData) => {
          const icon = String((card as any).vsmIcon ?? '');
          const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
          return card.tipo === 'atividade' && (['pcpm', 'nivelamento', 'info_eletro'].includes(icon) || /pcpm|mrp/i.test(label));
        };
        const isVerticalProductCard = (card: CardData) => (card as any).vsmIcon === 'produto' && (card as any).vsmOrientation === 'vertical';
        const isVerticalFlowCard = (card: CardData) => (card as any).vsmIcon === 'vertical_flow';
        const isTransportCard = (card: CardData) => ['caminhao', 'transporte'].includes((card as any).vsmIcon ?? '');
        const isDockedTransportCard = (card: CardData) => isTransportCard(card) && !!(card as any).transportDockParentId;
        const isExternalEntityCard = (card: CardData) => ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '');
        const isClientEntityCard = (card: CardData) => {
          const extType = String((card as any).extType ?? '').toUpperCase();
          return card.id === 'ext-cliente' || extType === 'CLIENTE' || card.tipo === 'card_final' || (card as any).vsmIcon === 'cliente';
        };
        const isCommunicationCard = (card: CardData) => card.tipo === 'atividade' && (card as any).vsmIcon === 'comunicacao';
        const isPcpmExternalPair =
          (isPcpmCard(s) && isExternalEntityCard(t)) ||
          (isExternalEntityCard(s) && isPcpmCard(t));
        const effectiveConnectionType = isPcpmExternalPair ? 'vsm_info' : c.connectionType;
        const isKanbanInfoConn = effectiveConnectionType === 'vsm_info' && Boolean((c as any).kanbanLoopEnabled);
        const sourceHandleForGeometry = isKanbanInfoConn ? 'top' : c.sourceHandle;
        // V210: no modo Futuro, kanban (retirada/produção) trafega pela zona de informação (topo→topo),
        // não cruzando lateralmente o fluxo de material. Em Atual/Kaizen mantém entrada lateral (right).
        const targetHandleForGeometry = isKanbanInfoConn
          ? (isFutureMode ? 'top' : 'right')
          : (c as any).targetHandle;
        const getExternalDataHeight = (card: CardData): number => {
          const demandMix = ((card as any).vsmDemandMix ?? []) as Array<{ qty?: number }>;
          const activeMixRows = demandMix.filter(item => Number(item?.qty) > 0).length;
          const hasProduct = !!((card as any).vsmProduto || activeMixRows > 0);
          const hasFrequency = !!(card as any).vsmFrequencia;
          const hasDemand = !!((card as any).vsmDemanda || (card as any).entrada);
          const hasDeliveryLot = Number((card as any).vsmDeliveryLot) > 0;
          const legacyRows = activeMixRows === 0
            ? [(card as any).vsmLinha4, (card as any).vsmLinha5].filter(Boolean).length
            : 0;
          const dataBoxHeight =
            (hasProduct ? 20 : 0) +
            (hasFrequency ? 20 : 0) +
            activeMixRows * 18 +
            (hasDemand ? 20 : 0) +
            (hasDeliveryLot ? 20 : 0) +
            legacyRows * 20;
          return dataBoxHeight > 0 ? Math.max(60, dataBoxHeight) : 0;
        };
        const getCardW = (card: CardData, isDiamond: boolean): number => {
          if (isDiamond) return 160;
          if (card.tipo === 'inventory') return INV_W_MAP[card.vsmIcon || ''] ?? 84;
          // Card.tsx renderiza card_inicial com container de 220px mesmo fora do classic.
          // card_final só usa 220px no classic; entidade final default usa a largura real do SVG.
          if (card.tipo === 'card_inicial') return 220;
          if (card.tipo === 'card_final' && card.layoutType === 'classic') return 220;
          if (isExternalEntityCard(card)) return VSM_ENTITY_PROCESS_WIDTH;
          if (isPcpmCard(card)) return 127;
          if (isCommunicationCard(card)) return 118;
          if (isProcessoCard(card)) return hasDataBoxField(card) ? VSM_PROCESS_DATABOX_WIDTH : VSM_ENTITY_PROCESS_WIDTH;
          if (isVerticalProductCard(card)) return 62;
          if (isVerticalFlowCard(card)) return 62;
          if (isDockedTransportCard(card)) return 176;
          if (isTransportCard(card)) return 110;
          if (isTimeScaleActive && card.calculatedWidth && card.calculatedWidth > 0) return card.calculatedWidth;
          return 240;
        };
        const getCardH = (card: CardData, isDiamond: boolean): number => {
          if (isDiamond) return 160;
          if (card.tipo === 'inventory') return INV_H_MAP[card.vsmIcon || ''] ?? 56;
          // card_final/card_inicial classic: altura do container = altura do shape externo
          if ((card.tipo === 'card_inicial' || card.tipo === 'card_final') && card.layoutType === 'classic') return 90 + getExternalDataHeight(card);
          if (isExternalEntityCard(card)) return 90 + getExternalDataHeight(card);
          if (isPcpmCard(card)) return 90;
          if (isCommunicationCard(card)) return 40;
          if (isProcessoCard(card)) return hasDataBoxField(card) ? getProcessDataBoxHeight() : VSM_PROCESS_HEIGHT;
          if (isVerticalProductCard(card)) return 220;
          if (isVerticalFlowCard(card)) return Math.max(120, Number((card as any).vsmFlowHeight) || 220);
          if (isDockedTransportCard(card)) return 72;
          if (isTransportCard(card)) return 60;
          return 180;
        };

        // Centro visual para conexões laterais (left/right).
        // Em cards com seção inferior (databox/tabela), manter âncora no corpo principal.
        const getVisualAnchorCenterY = (card: CardData, cardTopY: number, cardH: number): number => {
          if (card.tipo === 'inventory' && (card.vsmIcon ?? '') === 'estoque') {
            // Triangulo de estoque: ancorar no centro real do icone para nao inclinar o fluxo material.
            return cardTopY + cardH / 2;
          }
          if (isProcessoCard(card)) {
            return cardTopY + VSM_PROCESS_HEIGHT / 2;
          }
          if (isExternalEntityCard(card) || card.tipo === 'card_inicial' || card.tipo === 'card_final') {
            return cardTopY + 90 / 2;
          }
          return cardTopY + cardH / 2;
        };

        const sW = getCardW(s, isSourceDiamond);
        const sH = getCardH(s, isSourceDiamond);
        const tW = getCardW(t, isTargetDiamond);
        const tH = getCardH(t, isTargetDiamond);
        
        // Ajustar posição Y para considerar offset de +10px dos cards de decisão
        const sourceYOffset = isSourceDiamond ? 10 : 0;
        const targetYOffset = isTargetDiamond ? 10 : 0;

        // Em classic, apenas card_inicial renderiza com left: pos.x + 30 no Card.tsx.
        // card_final mantém left em pos.x.
        const getCardRenderOffsetX = (card: CardData): number =>
          (card.tipo === 'card_inicial' && card.layoutType === 'classic') ? 30 : 0;
        const sourcePosOffsetX = getCardRenderOffsetX(s);
        const targetPosOffsetX = getCardRenderOffsetX(t);

        const sourceAnchorCenterY = getVisualAnchorCenterY(s, sourcePos.y + sourceYOffset, sH);
        const targetAnchorCenterY = getVisualAnchorCenterY(t, targetPos.y + targetYOffset, tH);
        const sourceCenter = {
          x: sourcePos.x + sourcePosOffsetX + sW / 2 + (isExternalEntityCard(s) ? VSM_ENTITY_VISUAL_CENTER_NUDGE_X : 0),
          y: sourceAnchorCenterY,
        };
        const targetCenter = {
          x: targetPos.x + targetPosOffsetX + tW / 2 + (isExternalEntityCard(t) ? VSM_ENTITY_VISUAL_CENTER_NUDGE_X : 0),
          y: targetAnchorCenterY,
        };

        let sx: number, sy: number;
        let tx: number, ty: number;

        // Triângulo estoque: handles nas faces laterais do triângulo (25%/75% da largura)
        const isSourceTriangle = s.tipo === 'inventory' && s.vsmIcon === 'estoque';
        const isTargetTriangle = t.tipo === 'inventory' && t.vsmIcon === 'estoque';

        // PONTO DE SAÍDA (SOURCE)
        if (sIsClassic) {
          if (sourceHandleForGeometry === 'bottom') {
            sx = sourcePos.x + sourcePosOffsetX + sW / 2;
            sy = sourcePos.y + sourceYOffset + sH + (isSourceDiamond ? 2.5 : 0);
          } else if ((sourceHandleForGeometry as string) === 'top') {
            sx = sourcePos.x + sourcePosOffsetX + sW / 2;
            sy = sourcePos.y + sourceYOffset;
          } else if ((sourceHandleForGeometry as string) === 'left') {
            sx = isSourceTriangle ? sourcePos.x + sW * 0.25 : sourcePos.x + sourcePosOffsetX;
            sy = sourceCenter.y;
          } else {
            const edgePoint = getDiamondConnectionPoint(sourceCenter, 160, 160, { x: sourceCenter.x + 100, y: sourceCenter.y }, 2.5, true);
            sx = isSourceDiamond ? edgePoint.x : isSourceTriangle ? sourcePos.x + sW * 0.75 : sourcePos.x + sourcePosOffsetX + sW;
            sy = isSourceDiamond ? edgePoint.y : sourceCenter.y;

            const exitOffset = exitOffsets.get(c.id) || 0;
            sy += exitOffset;
          }
        } else {
          if (sourceHandleForGeometry === 'bottom') {
            sx = sourceCenter.x;
            sy = sourcePos.y + sourceYOffset + sH;
            if (isSourceDiamond) sy = sourceCenter.y;
          } else if ((sourceHandleForGeometry as string) === 'top') {
            sx = sourceCenter.x;
            sy = sourcePos.y + sourceYOffset;
          } else if ((sourceHandleForGeometry as string) === 'left') {
            sx = isSourceTriangle ? sourcePos.x + sW * 0.25 : sourcePos.x + sourcePosOffsetX;
            sy = sourceCenter.y;
          } else {
            sx = isSourceTriangle ? sourcePos.x + sW * 0.75 : sourcePos.x + sourcePosOffsetX + sW;
            sy = sourceCenter.y;

            const exitOffset = exitOffsets.get(c.id) || 0;
            sy += exitOffset;
          }
        }

        // PONTO DE ENTRADA (TARGET)
        // Se targetHandle está definido, usar o handle exato clicado pelo usuário
        const targetHandleDir = targetHandleForGeometry as 'left' | 'right' | 'top' | 'bottom' | undefined;
        if (targetHandleDir && !isTargetDiamond) {
          switch (targetHandleDir) {
            case 'top':    tx = targetPos.x + targetPosOffsetX + tW / 2; ty = targetPos.y + targetYOffset; break;
            case 'bottom': tx = targetPos.x + targetPosOffsetX + tW / 2; ty = targetPos.y + targetYOffset + tH; break;
            case 'right':  tx = isTargetTriangle ? targetPos.x + tW * 0.75 : targetPos.x + targetPosOffsetX + tW;     ty = targetCenter.y; break;
            case 'left':   tx = isTargetTriangle ? targetPos.x + tW * 0.25 : targetPos.x + targetPosOffsetX;          ty = targetCenter.y; break;
          }
          // Distribuição de setas que entram no mesmo handle: desloca perpendicular ao lado
          const entryOff = entryOffsets.get(c.id);
          if (entryOff && entryOff !== 0) {
            if (targetHandleDir === 'top' || targetHandleDir === 'bottom') {
              const halfW = tW / 2 - 10; // margem para não encostar nas bordas
              tx += Math.max(-halfW, Math.min(halfW, entryOff));
            } else {
              const halfH = tH / 2 - 10;
              ty += Math.max(-halfH, Math.min(halfH, entryOff));
            }
          }
        } else if (tIsClassic) {
          if (isTargetDiamond) {
            const dx = targetCenter.x - sourceCenter.x;
            const isVertical = Math.abs(dx) < 60;
            if (isVertical) {
              tx = targetPos.x + tW / 2;
              ty = targetCenter.y > sourceCenter.y ? targetPos.y - 2.5 : targetPos.y + tH + 2.5;
            } else {
              const edgePoint = getDiamondConnectionPoint(targetCenter, 160, 160, sourceCenter, 2.5, true);
              tx = edgePoint.x; ty = edgePoint.y;
            }
          } else if (isTargetTriangle) {
            // Entrada horizontal no triângulo: usar face lateral esquerda
            tx = targetPos.x + tW * 0.25;
            ty = targetCenter.y;
          } else {
            tx = targetPos.x + targetPosOffsetX;
            ty = targetCenter.y;
          }
        } else {
          if (isTargetDiamond) {
            const dx = targetCenter.x - sourceCenter.x;
            if (Math.abs(dx) < 60) {
              tx = targetCenter.x;
              ty = targetCenter.y > sourceCenter.y ? targetPos.y + targetYOffset + tH/2 - tH/2 - 12 : targetPos.y + targetYOffset + tH/2 + tH/2 + 12;
            } else {
              tx = targetPos.x + targetPosOffsetX; ty = targetCenter.y;
            }
          } else if (isTargetTriangle) {
            tx = targetPos.x + tW * 0.25;
            ty = targetCenter.y;
          } else {
            tx = targetPos.x + targetPosOffsetX; ty = targetCenter.y;
          }
        }

        if (isKanbanInfoConn) {
          const getConvertedSupermarketAnchor = (
            card: CardData,
            pos: { x: number; y: number },
            width: number,
            height: number,
            handle: 'left' | 'right' | 'top' | 'bottom' | undefined,
          ) => {
            if (card.vsmIcon !== 'estoque' || (card as any).vsmConvertedToIcon !== 'supermercado') return null;
            // V210: no Futuro o supermercado está centrado no bbox — usar âncora padrão (return null).
            if (isFutureMode) return null;
            const offset = ((card as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
            const marketW = Math.round(width * 0.92);
            const marketH = Math.round(height * 0.98);
            const marketLeft = Math.round(width * 0.46) + (Number(offset.x) || 0);
            const marketTop = -Math.round(height * 1.08) + (Number(offset.y) || 0);
            const left = pos.x + marketLeft;
            const top = pos.y + marketTop;
            switch (handle) {
              case 'top': return { x: left + marketW / 2, y: top };
              case 'bottom': return { x: left + marketW / 2, y: top + marketH };
              case 'left': return { x: left, y: top + marketH / 2 };
              case 'right': return { x: left + marketW, y: top + marketH / 2 };
              default: return { x: left, y: top + marketH / 2 };
            }
          };
          const sourceMarketAnchor = getConvertedSupermarketAnchor(s, sourcePos, sW, sH, sourceHandleForGeometry as any);
          const targetMarketAnchor = getConvertedSupermarketAnchor(t, targetPos, tW, tH, targetHandleDir);
          if (sourceMarketAnchor) {
            sx = sourceMarketAnchor.x;
            sy = sourceMarketAnchor.y;
          }
          if (targetMarketAnchor) {
            tx = targetMarketAnchor.x;
            ty = targetMarketAnchor.y;
          }
        }

        // 📏 GAP PADRONIZADO para setas de INFORMAÇÃO (vsm_info) e KANBAN (loop info):
        // afasta os pontos de saída/chegada em 8 px da borda do card, garantindo
        // uma respiração visual consistente entre a ponta da seta e o card.
        // Aplica apenas quando o handle é cardinal (top/bottom/left/right) e a
        // conexão não foi sobrescrita por merge/manual waypoints/forceRadial.
        if (
          effectiveConnectionType === 'vsm_info' &&
          !c.mergeToConnection &&
          !(Array.isArray(c.waypoints) && c.waypoints.length > 0) &&
          !(c as any).forceRadial
        ) {
          const INFO_KANBAN_ENDPOINT_GAP = 6.4;
          // Para cards VSM externos renderizados em container legado de 220px
          // (card_inicial/card_final classic com vsmIcon), o ícone visual (127px)
          // fica CENTRALIZADO dentro do container. As bordas do container ficam
          // ~46.5px afastadas da borda visual do ícone. Para ancorar a seta a 8px
          // da borda VISUAL, deslocamos os endpoints horizontais INWARD a partir
          // da borda do container.
          const VSM_ENTITY_VISUAL_WIDTH = 127;
          const isLegacyContainerVsm = (card: CardData, containerW: number): boolean => {
            const vsm = (card as any).vsmIcon as string | undefined;
            if (!vsm) return false;
            if (!['externo', 'fornecedor', 'cliente'].includes(vsm)) return false;
            return containerW > VSM_ENTITY_VISUAL_WIDTH + 1;
          };
          const horizGapFor = (card: CardData, containerW: number): number => {
            if (!isLegacyContainerVsm(card, containerW)) return INFO_KANBAN_ENDPOINT_GAP;
            const halfDiff = (containerW - VSM_ENTITY_VISUAL_WIDTH) / 2;
            // gap visual desejado = 8px → deslocamento a partir da borda do container = -(halfDiff - 8)
            return -(halfDiff - INFO_KANBAN_ENDPOINT_GAP);
          };
          const sHorizGap = horizGapFor(s, sW);
          const tHorizGap = horizGapFor(t, tW);
          const applyOutwardGap = (
            handle: string | undefined,
            x: number,
            y: number,
            horizGap: number,
          ): { x: number; y: number } => {
            switch (handle) {
              case 'top':    return { x, y: y - INFO_KANBAN_ENDPOINT_GAP };
              case 'bottom': return { x, y: y + INFO_KANBAN_ENDPOINT_GAP };
              case 'left':   return { x: x - horizGap, y };
              case 'right':  return { x: x + horizGap, y };
              default:       return { x, y };
            }
          };
          const sShift = applyOutwardGap(sourceHandleForGeometry as string | undefined, sx, sy, sHorizGap);
          const tShift = applyOutwardGap(targetHandleForGeometry as string | undefined, tx, ty, tHorizGap);
          sx = sShift.x; sy = sShift.y;
          tx = tShift.x; ty = tShift.y;
        }

        // Quando targetHandle foi definido (usuário clicou handles explícitos),
        // desenhar linha direta source→target, sem roteamento ortogonal complexo.
        const hasExplicitTargetHandle = !!targetHandleForGeometry && !isTargetDiamond;

        // 🎯 MERGE TO CONNECTION: Se a conexão tem merge point, usar como destino
        // Isso cria uma junção em T onde esta conexão se conecta a uma seta existente
        let hasMergePoint = false;
        let mergeArrowAngle = 0;
        if (c.mergeToConnection && c.mergeToConnection.mergePoint) {
          tx = c.mergeToConnection.mergePoint.x;
          ty = c.mergeToConnection.mergePoint.y;
          hasMergePoint = true;
          
          // Calcular ângulo da seta baseado na direção de chegada
          // Isso será usado para orientar a ponta da seta no merge point
          // Por padrão, assumimos entrada pela esquerda (-180° = seta para direita)
          mergeArrowAngle = 0; // Seta padrão para direita
        }

        // CRÍTICO: Obstáculos devem usar calculatedWidth quando Time Scale está ativo
        // 🎯 FLATTENED VIEW: Usar visibleNodes para obstáculos (inclui filhos expandidos)
        const obstacles = visibleNodes
          .filter(card => card.id !== s.id && card.id !== t.id && card.status === 'ativo')
          .map(card => {
            const isCardDiamond = card.tipo === 'decisao';
            const cardW = isCardDiamond ? 160
              : card.tipo === 'inventory' ? (INV_W_MAP[card.vsmIcon || ''] ?? 84)
              : isExternalEntityCard(card) ? VSM_ENTITY_PROCESS_WIDTH
              : isCommunicationCard(card) ? 118
              : isVerticalProductCard(card) ? 62
              : isVerticalFlowCard(card) ? 62
              : isDockedTransportCard(card) ? 176
              : isTransportCard(card) ? 110
              : isTimeScaleActive && card.calculatedWidth && card.calculatedWidth > 0 ? card.calculatedWidth
              : 240;
            const cardPos = card.posicao || (card as any).position || { x: 0, y: 0 };
            return { 
              id: card.id, 
              x: cardPos.x, 
              y: cardPos.y, 
              width: cardW, // Usa calculatedWidth quando Time Scale está ativo
              height: isCardDiamond ? 160 : isExternalEntityCard(card) ? 90 + getExternalDataHeight(card) : isCommunicationCard(card) ? 40 : isVerticalProductCard(card) ? 220 : isVerticalFlowCard(card) ? Math.max(120, Number((card as any).vsmFlowHeight) || 220) : isDockedTransportCard(card) ? 72 : isTransportCard(card) ? 60 : 160 
            };
          });
        let d: string;
        // Conexões de material no mapa FACTORY sempre fluem source→target independente de posição X.
        // Só considerar isReturn para fluxos não-material (workflow comum), onde posição X determina retorno.
        const isMaterialConnection = effectiveConnectionType === 'material';
        const isPushMaterialConnection = effectiveConnectionType === 'push';
        const isMaterialFlowConnection = isMaterialConnection || isPushMaterialConnection;
        const isReturn = !isPcpmExternalPair && !isMaterialFlowConnection && (targetCenter.x < sourceCenter.x);
        const getLateralMaterialAnchor = (
          card: CardData,
          cardPos: Position,
          renderOffsetX: number,
          cardW: number,
          centerY: number,
          side: 'left' | 'right'
        ) => {
          const isStockTriangle = card.tipo === 'inventory' && (card.vsmIcon ?? '') === 'estoque';
          const x = side === 'right'
            ? isStockTriangle ? cardPos.x + cardW * 0.75 : cardPos.x + renderOffsetX + cardW
            : isStockTriangle ? cardPos.x + cardW * 0.25 : cardPos.x + renderOffsetX;
          return { x, y: centerY };
        };
        // vsm_transport_delivery sempre usa roteamento stacked vertical — nunca paralelo
        const isTransportDeliveryConnEarly = effectiveConnectionType === 'vsm_transport_delivery';
        
        // Detectar se é uma conexão paralela (múltiplas conexões saindo pela direita do mesmo card)
        // Contar TODAS as conexões que saem pela direita (incluindo a atual)
        const allRightConnections = s.conexoes_saida.filter(
          conn => conn.sourceHandle === 'right'
        );
        // É paralelo se há mais de uma conexão pela direita E esta conexão também sai pela direita
        // IMPORTANTE: Não considerar conexões de retorno (isReturn) como paralelas
        // IMPORTANTE: Não considerar vsm_transport_delivery como paralela — usa rota stacked sempre
        const isParallel = allRightConnections.length > 1 && c.sourceHandle === 'right' && !isReturn && !isTransportDeliveryConnEarly;
        
        // CRÍTICO: Quando Time Scale está ativo, aplicar caminho especial para TODAS as conexões horizontais
        // não apenas para conexões paralelas, porque os cards aumentaram de tamanho e precisam compensar
        // IMPORTANTE: vsm_transport_delivery nunca usa time scale path — usa rota stacked sempre
        const needsTimeScalePath = isTimeScaleActive && c.sourceHandle === 'right' && !isReturn && !isTransportDeliveryConnEarly;
        
        const parallelIndex = isParallel ? allRightConnections.findIndex(conn => conn.id === c.id) : -1;
        
        // Debug logs comentados para melhor performance
        // if (isParallel) {
        //   console.log(`[useEdgeCalculations] ✅ CONEXÃO PARALELA DETECTADA: card ${s.id} -> ${t.id}`, {
        //     totalConexoesDireita: allRightConnections.length,
        //     indice: parallelIndex,
        //     sourceHandle: c.sourceHandle,
        //     isReturn,
        //     sx,
        //     sy,
        //     tx,
        //     ty,
        //     sW,
        //     tW,
        //     sourceCalculatedWidth: s.calculatedWidth,
        //     targetCalculatedWidth: t.calculatedWidth,
        //     isTimeScaleActive
        //   });
        // } else if (needsTimeScalePath) {
        //   console.log(`[useEdgeCalculations] ⚠️ TIME SCALE PATH NECESSÁRIO (não paralela, mas Time Scale ativo): card ${s.id} -> ${t.id}`);
        // }
        
        // Em links operacionais com entidade externa, forçar eixo vertical central
        // e ignorar handles/waypoints legados para não sair pela lateral.
        const isExplicitLateralInfoConn =
          effectiveConnectionType === 'vsm_info' &&
          ['left', 'right'].includes(String(c.sourceHandle ?? '')) &&
          ['left', 'right'].includes(String((c as any).targetHandle ?? '')) &&
          (isPcpmCard(s) || isPcpmCard(t) || isExternalEntityCard(s) || isExternalEntityCard(t) || isCommunicationCard(s) || isCommunicationCard(t));

        const isPcpmProcessInfoConn =
          effectiveConnectionType === 'vsm_info' &&
          isPcpmCard(s) &&
          isProcessoCard(t);

        if (isPcpmCard(s) && isExternalEntityCard(t) && c.sourceHandle === 'bottom') return;

        const forceExternalVerticalEarly =
          (isExternalEntityCard(s) || isExternalEntityCard(t)) &&
          !(isExternalEntityCard(s) && isExternalEntityCard(t)) &&
          effectiveConnectionType !== 'vsm_info';

        const hasManualWaypoints = Array.isArray(c.waypoints) && c.waypoints.length > 0;
        const isManualCommand = !!(c as any).manualCommand;
        const forceRadial = !!(c as any).forceRadial;
        const shouldForceStraightInfo = effectiveConnectionType === 'vsm_info' && !hasManualWaypoints && !hasMergePoint;
        const shouldForceStraightManual = isManualCommand && !hasManualWaypoints && !hasMergePoint;

        if (isMaterialFlowConnection && !isReturn) {
          const flowsRight = targetCenter.x >= sourceCenter.x;
          const sourceAnchor = getLateralMaterialAnchor(
            s,
            sourcePos,
            sourcePosOffsetX,
            sW,
            sourceCenter.y,
            flowsRight ? 'right' : 'left'
          );
          const targetAnchor = getLateralMaterialAnchor(
            t,
            targetPos,
            targetPosOffsetX,
            tW,
            targetCenter.y,
            flowsRight ? 'left' : 'right'
          );

          sx = sourceAnchor.x;
          sy = sourceAnchor.y;
          tx = targetAnchor.x;
          ty = targetAnchor.y;

          const sharedY = Math.round((sy + ty) / 2);
          sy = sharedY;
          ty = sharedY;

          const SOURCE_CLEARANCE = t.tipo === 'inventory' ? 56 : 0;
          const TARGET_ARROW_CLEARANCE = t.tipo === 'inventory' ? 2 : isPushMaterialConnection ? 20 : 14;
          if (SOURCE_CLEARANCE > 0 && Math.abs(tx - sx) > SOURCE_CLEARANCE * 2) {
            sx += tx > sx ? SOURCE_CLEARANCE : -SOURCE_CLEARANCE;
          }
          if (Math.abs(tx - sx) > TARGET_ARROW_CLEARANCE * 2) {
            tx += tx > sx ? -TARGET_ARROW_CLEARANCE : TARGET_ARROW_CLEARANCE;
          }

          if (t.tipo === 'inventory') {
            const STOCK_ENTRY_LENGTH = 24;
            sx = tx > sx ? tx - STOCK_ENTRY_LENGTH : tx + STOCK_ENTRY_LENGTH;
          }

          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (hasManualWaypoints) {
          d = [`M ${sx.toFixed(1)} ${sy.toFixed(1)}`]
            .concat((c.waypoints ?? []).map(wp => `L ${Number(wp.x).toFixed(1)} ${Number(wp.y).toFixed(1)}`))
            .concat(`L ${tx.toFixed(1)} ${ty.toFixed(1)}`)
            .join(' ');
        } else if (forceRadial) {
          sx = sourceCenter.x;
          sy = sourceCenter.y;
          tx = targetCenter.x;
          ty = targetCenter.y;
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (isPcpmProcessInfoConn) {
          sx = sourcePos.x + sourcePosOffsetX + sW / 2;
          sy = sourcePos.y + sourceYOffset + sH + 6.4;
          tx = targetPos.x + targetPosOffsetX + tW / 2;
          ty = targetPos.y + targetYOffset - 6.4;
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (shouldForceStraightManual) {
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (shouldForceStraightInfo) {
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (isExplicitLateralInfoConn) {
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (forceExternalVerticalEarly) {
          const TRANSPORT_ENDPOINT_OFFSET = 25;
          const DEFAULT_EDGE_PAD = 12;
          const sourceCenterX = sourcePos.x + sourcePosOffsetX + sW / 2;
          const targetCenterX = targetPos.x + targetPosOffsetX + tW / 2;
          const sourceCenterY = sourcePos.y + sourceYOffset + sH / 2;
          const targetCenterY = targetPos.y + targetYOffset + tH / 2;
          const targetBelowSource = targetCenterY >= sourceCenterY;

          sx = sourceCenterX;
          tx = targetCenterX;
          if (targetBelowSource) {
            const sourceBottom = sourcePos.y + sourceYOffset + sH;
            const targetTop = targetPos.y + targetYOffset;
            const freeGap = Math.max(0, targetTop - sourceBottom);
            const balancedPad = effectiveConnectionType === 'vsm_transport' ? TRANSPORT_ENDPOINT_OFFSET : DEFAULT_EDGE_PAD;
            sy = sourceBottom + balancedPad;
            ty = targetTop - balancedPad;
          } else {
            const sourceTop = sourcePos.y + sourceYOffset;
            const targetBottom = targetPos.y + targetYOffset + tH;
            const freeGap = Math.max(0, sourceTop - targetBottom);
            const balancedPad = effectiveConnectionType === 'vsm_transport' ? TRANSPORT_ENDPOINT_OFFSET : DEFAULT_EDGE_PAD;
            sy = sourceTop - balancedPad;
            ty = targetBottom + balancedPad;
          }
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (hasExplicitTargetHandle) {
          // Linha direta entre os dois handles exatos selecionados pelo usuário — sem roteamento
          d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
        } else if (c.waypoints && c.waypoints.length > 0) {
          // Waypoints: garantir que cada segmento seja ortogonal
          let waypointPath = `M ${sx} ${sy}`;
          let lastX = sx;
          let lastY = sy;
          c.waypoints.forEach(wp => {
            // Movimento horizontal primeiro, depois vertical (ou vice-versa) para garantir ortogonalidade
            if (Math.abs(wp.x - lastX) > 0.1) {
              waypointPath += ` L ${wp.x} ${lastY}`;
              lastX = wp.x;
            }
            if (Math.abs(wp.y - lastY) > 0.1) {
              waypointPath += ` L ${lastX} ${wp.y}`;
              lastY = wp.y;
            }
          });
          
          // Conectar ao destino de forma ortogonal (entrada sempre pela esquerda)
          // Primeiro mover verticalmente, depois horizontalmente
          if (Math.abs(ty - lastY) > 0.1) {
            waypointPath += ` L ${lastX} ${ty}`;
            lastY = ty;
          }
          if (Math.abs(tx - lastX) > 0.1) {
            waypointPath += ` L ${tx} ${lastY}`;
          }
          d = waypointPath;
        } else if (isReturn && c.sourceHandle === 'right') {
          // RETORNO PELA DIREITA: Usar path básico, a fusão será feita no pós-processamento
          // O sistema de "Tronco de Retorno Único" vai agrupar e fusionar depois
          d = getReturnLoopPath(sx, sy, tx, ty, c.sourceHandle, sH, tW, obstacles);
        } else if (isParallel || needsTimeScalePath) {
          // Caminho para cards paralelos: ORTOGONAL (sempre 90 graus, nunca diagonal)
          // CORREÇÃO: Garantir que estamos usando calculatedWidth corretamente em TODOS os cálculos
          
          // CRÍTICO: sx já foi calculado usando sW (que considera calculatedWidth)
          // targetEntryX deve ser o lado esquerdo do card destino
          const targetEntryX = targetPos.x; // Lado esquerdo do card destino (ponto de entrada)
          // CRÍTICO: Incluir targetYOffset para alinhar corretamente com cards de decisão
          const targetEntryY = targetPos.y + targetYOffset + tH / 2; // Centro vertical do card destino
          
          // DEBUG: Logs comentados para melhor performance
          // console.log(`[PARALLEL EDGE DEBUG] Card ${s.id} -> ${t.id}`, {
          //   source: {
          //     id: s.id,
          //     pos: sourcePos,
          //     width: sW,
          //     exit: { x: sx, y: sy },
          //     calculatedWidth: s.calculatedWidth,
          //     isTimeScaleActive
          //   },
          //   target: {
          //     id: t.id,
          //     pos: targetPos,
          //     width: tW,
          //     entry: { x: targetEntryX, y: targetEntryY },
          //     calculatedWidth: t.calculatedWidth
          //   }
          // });
          
          // Verificar TODOS os cards que podem bloquear o caminho
          // CRÍTICO: Usar calculatedWidth quando Time Scale está ativo
          // 🎯 FLATTENED VIEW: Usar visibleNodes para obstáculos
          const allObstacles = visibleNodes
            .filter(card => card.id !== s.id && card.id !== t.id && card.status === 'ativo')
            .map(card => {
              const isCardDiamond = card.tipo === 'decisao';
              // CRÍTICO: Sempre usar calculatedWidth quando Time Scale está ativo
              const cardW = isTimeScaleActive && card.calculatedWidth && card.calculatedWidth > 0 && !isCardDiamond
                ? card.calculatedWidth
                : (isCardDiamond ? 160 : 240);
              return {
                id: card.id,
                x: card.posicao?.x, // Posição X do card (canto superior esquerdo)
                y: card.posicao?.y, // Posição Y do card (canto superior esquerdo)
                width: cardW, // Largura do card (considera calculatedWidth)
                height: 160,
                right: card.posicao?.x + cardW, // Lado direito do card (x + width)
                bottom: card.posicao?.y + 160 // Lado inferior do card (y + height)
              };
            });
          
          // DEBUG: Log dos obstáculos (comentado)
          // console.log(`[PARALLEL EDGE DEBUG] Obstáculos:`, allObstacles.map(o => ({
          //   id: o.id,
          //   x: o.x,
          //   width: o.width,
          //   right: o.right,
          //   y: o.y,
          //   bottom: o.bottom
          // })));
          
          // CRÍTICO: Encontrar cards que REALMENTE estão bloqueando o caminho
          // Um card bloqueia APENAS se:
          // 1. Está na mesma faixa vertical do target (não apenas acima do source)
          // 2. E está horizontalmente entre source e target
          const blockingCardsAbove = allObstacles.filter(obstacle => {
            // Verificar se está na faixa horizontal entre source e target
            const minX = Math.min(sx, targetEntryX);
            const maxX = Math.max(sx, targetEntryX);
            const isBetweenHorizontally = obstacle.right > minX && obstacle.x < maxX;
            
            // CORREÇÃO: Verificar se está na mesma faixa vertical do TARGET (não do source)
            // Um card só bloqueia se está entre o source e o target verticalmente
            const isInTargetVerticalRange = 
              (obstacle.y >= Math.min(sy, targetEntryY) - 20) && 
              (obstacle.bottom <= Math.max(sy, targetEntryY) + 20);
            
            return isBetweenHorizontally && isInTargetVerticalRange;
          });
          
          // DEBUG: Log dos bloqueadores (comentado)
          // console.log(`[PARALLEL EDGE DEBUG] Cards bloqueadores acima:`, blockingCardsAbove.map(c => ({
          //   id: c.id,
          //   x: c.x,
          //   width: c.width,
          //   right: c.right,
          //   y: c.y,
          //   bottom: c.bottom,
          //   isAboveSource: c.bottom < sy,
          //   isBetweenHorizontally: c.right > Math.min(sx, targetEntryX) && c.x < Math.max(sx, targetEntryX)
          // })));
          
          // Calcular altura segura: passar por baixo dos cards bloqueadores
          let horizontalY = sy; // Por padrão, manter na mesma altura do source
          
          if (blockingCardsAbove.length > 0) {
            // Encontrar o card mais baixo (maior bottom) entre os bloqueadores acima
            const lowestCardBottom = Math.max(...blockingCardsAbove.map(card => card.bottom));
            const SAFETY_MARGIN = 20;
            horizontalY = lowestCardBottom + SAFETY_MARGIN;
          }
          
          // Garantir que não estamos acima do source
          if (horizontalY < sy) {
            horizontalY = sy + 50;
          }
          
          // CRÍTICO: Encontrar o card bloqueador mais à direita e ir até o lado direito dele
          let finalHorizontalX = targetEntryX; // Por padrão, ir até a entrada do destino
          
          if (blockingCardsAbove.length > 0) {
            // Encontrar o card bloqueador mais à direita (maior right)
            const rightmostBlockingCard = blockingCardsAbove.reduce((max, card) => {
              return card.right > max.right ? card : max;
            }, blockingCardsAbove[0]);
            
            // Calcular o lado direito do card bloqueador mais à direita
            const rightmostCardRight = rightmostBlockingCard.right; // Já calculado como x + width
            const SAFETY_MARGIN_X = 20;
            
            // Ir até o lado direito do card bloqueador + margem antes de subir
            finalHorizontalX = Math.max(targetEntryX, rightmostCardRight + SAFETY_MARGIN_X);
            
            // DEBUG: Log do cálculo final (comentado)
            // console.log(`[PARALLEL EDGE DEBUG] Cálculo final:`, {
            //   rightmostBlockingCard: {
            //     id: rightmostBlockingCard.id,
            //     x: rightmostBlockingCard.x,
            //     width: rightmostBlockingCard.width,
            //     right: rightmostCardRight
            //   },
            //   finalHorizontalX,
            //   targetEntryX,
            //   horizontalY,
            //   sourceExit: { x: sx, y: sy }
            // });
          }
          
          // CORREÇÃO: Se NÃO há bloqueadores, usar CAMINHO SIMPLES (direto)
          // Se HÁ bloqueadores, usar CAMINHO COMPLEXO (desviar)
          
          if (blockingCardsAbove.length === 0) {
            // CAMINHO SIMPLES: Ir direto do source ao target
            
            // Verificar se source e target estão na mesma altura (tolerância de 5px)
            const sameHeight = Math.abs(sy - targetEntryY) < 5;
            
            if (sameHeight) {
              // Cards na mesma linha: caminho direto horizontal
              d = `M ${sx} ${sy} L ${targetEntryX} ${targetEntryY}`;
            } else {
              // Cards em alturas diferentes: usar cotovelo no meio do vão
              const midX = (sx + targetEntryX) / 2;
              d = `M ${sx} ${sy} 
                   L ${midX} ${sy} 
                   L ${midX} ${targetEntryY}
                   L ${targetEntryX} ${targetEntryY}`;
            }
          } else {
            // CAMINHO COMPLEXO: Desviar dos bloqueadores
            const safeFinalX = finalHorizontalX;
            
            // Verificar se sy e horizontalY são iguais (não precisa descer)
            const needsVerticalDescent = Math.abs(sy - horizontalY) > 5;
            // Verificar se horizontalY e targetEntryY são iguais (não precisa subir)
            const needsVerticalAscent = Math.abs(horizontalY - targetEntryY) > 5;
            // Verificar se safeFinalX e targetEntryX são iguais (não precisa horizontal final)
            const needsFinalHorizontal = Math.abs(safeFinalX - targetEntryX) > 5;
            
            // Construir path otimizado removendo segmentos de comprimento zero
            let pathPoints = [`M ${sx} ${sy}`];
            
            if (needsVerticalDescent) {
              pathPoints.push(`L ${sx} ${horizontalY}`);
            }
            pathPoints.push(`L ${safeFinalX} ${needsVerticalDescent ? horizontalY : sy}`);
            
            if (needsVerticalAscent) {
              pathPoints.push(`L ${safeFinalX} ${targetEntryY}`);
            }
            
            if (needsFinalHorizontal) {
              pathPoints.push(`L ${targetEntryX} ${targetEntryY}`);
            }
            
            d = pathPoints.join(' ');
          }
          
          // Atualizar tx e ty para o ponto de entrada correto
          tx = targetEntryX;
          ty = targetEntryY;
        } else if ((currentLayout === 'classic' || currentLayout === 'blueprint' || currentLayout === 'sketch') && (
          effectiveConnectionType === 'material' || effectiveConnectionType === 'push' ||
          effectiveConnectionType === 'vsm_transport_delivery' ||
          effectiveConnectionType === 'vsm_transport' ||
          effectiveConnectionType === 'vsm_fifo' ||
          effectiveConnectionType === 'vsm_info' ||
          effectiveConnectionType === undefined || effectiveConnectionType === null
        )) {
          // FACTORY: product/material flow is horizontal by default, but supports stacked vertical flow.
          // sW/tW return exact visual widths — card container matches visual bounds
          const sourceCenterX = sourcePos.x + sourcePosOffsetX + sW / 2;
          const targetCenterX = targetPos.x + targetPosOffsetX + tW / 2;
          // Y: for PD card connect at processo body center (top body matches 90px process card)
          const PD_BODY_CENTER_Y = 45; // centro do corpo do processo (sem puxar para databox)
          const getCardCenterY = (card: CardData, posY: number, h: number): number => {
            if (isProcessoCard(card) && hasDataBoxField(card)) return posY + PD_BODY_CENTER_Y;
            return posY + h / 2;
          };
          const sourceCenterY = getCardCenterY(s, sourcePos.y, sH);
          const targetCenterY = getCardCenterY(t, targetPos.y, tH);
          const centerDx = Math.abs(targetCenterX - sourceCenterX);
          const centerDy = Math.abs(targetCenterY - sourceCenterY);
          // vsm_transport_delivery always routes vertically (expedição → cliente)
          const isTransportDeliveryConn = effectiveConnectionType === 'vsm_transport_delivery';
          const isExternalToInventory =
            (isExternalEntityCard(s) && t.tipo === 'inventory') ||
            (isExternalEntityCard(t) && s.tipo === 'inventory');
          const isExpeditionToExternal =
            (isExpeditionCard(s) && isExternalEntityCard(t)) ||
            (isExpeditionCard(t) && isExternalEntityCard(s));
          const involvesExternalEntity = isExternalEntityCard(s) || isExternalEntityCard(t);
          const isExternalToExternal = isExternalEntityCard(s) && isExternalEntityCard(t);
          const forceVerticalEntityFlow =
            involvesExternalEntity &&
            !isExternalToExternal &&
            effectiveConnectionType !== 'vsm_info';
          const forceVerticalTransport =
            effectiveConnectionType === 'vsm_transport' && (isExternalToInventory || isExpeditionToExternal);

          const isStackedFlow = isTransportDeliveryConn || forceVerticalTransport || forceVerticalEntityFlow ||
            (centerDy > centerDx && centerDx <= Math.max(sW, tW) * 0.6);

          const TRANSPORT_GAP_REDUCTION = 0.40;
          const TRANSPORT_ENDPOINT_OFFSET = 25;
          const DEFAULT_EDGE_PAD = 12;

          if (isStackedFlow) {
            if (isExpeditionToExternal) {
              const externalCard = isExternalEntityCard(s) ? s : t;
              const internalCard = isExternalEntityCard(s) ? t : s;
              const externalPos = externalCard.posicao;
              const internalPos = internalCard.posicao;
              const externalW = getCardW(externalCard, externalCard.tipo === 'decisao');
              const externalH = getCardH(externalCard, externalCard.tipo === 'decisao');
              const internalH = getCardH(internalCard, internalCard.tipo === 'decisao');
              const externalOffsetX = getCardRenderOffsetX(externalCard);
              const internalOffsetX = getCardRenderOffsetX(internalCard);
              const externalCenterX = externalPos.x + externalOffsetX + externalW / 2;
              const verticalGap = effectiveConnectionType === 'vsm_transport' ? TRANSPORT_ENDPOINT_OFFSET : 18;

              sx = externalCenterX;
              tx = externalCenterX;
              if (isExternalEntityCard(t)) {
                sy = internalPos.y + sourceYOffset - verticalGap;
                ty = externalPos.y + targetYOffset + externalH + verticalGap;
              } else {
                sy = externalPos.y + sourceYOffset + externalH + verticalGap;
                ty = internalPos.y + targetYOffset - verticalGap;
              }
              d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
            } else {
            const targetBelowSource = targetCenterY >= sourceCenterY;
            sx = sourceCenterX;
            tx = sx;
            if (targetBelowSource) {
              const sourceBottom = sourcePos.y + sourceYOffset + sH;
              const targetTop = targetPos.y + targetYOffset;
              const freeGap = Math.max(0, targetTop - sourceBottom);
              const balancedPad = forceVerticalTransport
                ? TRANSPORT_ENDPOINT_OFFSET
                : DEFAULT_EDGE_PAD;
              sy = sourceBottom + balancedPad;
              ty = targetTop - balancedPad;
            } else {
              const sourceTop = sourcePos.y + sourceYOffset;
              const targetBottom = targetPos.y + targetYOffset + tH;
              const freeGap = Math.max(0, sourceTop - targetBottom);
              const balancedPad = forceVerticalTransport
                ? TRANSPORT_ENDPOINT_OFFSET
                : DEFAULT_EDGE_PAD;
              sy = sourceTop - balancedPad;
              ty = targetBottom + balancedPad;
            }
            d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} L ${tx.toFixed(1)} ${ty.toFixed(1)}`;
            }
          } else {
            // Fluxo horizontal: sai da borda direita do source, entra na borda esquerda do target
            // EDGE_PAD adaptativo: nunca ultrapassar 1/3 do gap real entre cards
            const rawGap = targetPos.x - (sourcePos.x + sW);
            const adaptivePad = rawGap > 0
              ? Math.min(6, Math.max(3, rawGap / 8))
              : 3;
            const visualSx = sourcePos.x + sW + 2;   // inicia fora da borda para manter ponta visível
            const visualTx = targetPos.x - adaptivePad;
            const sharedY = Math.round((sourceCenterY + targetCenterY) / 2);
            sy = sharedY;
            ty = sharedY;
            d = `M ${visualSx.toFixed(1)} ${sharedY} L ${visualTx.toFixed(1)} ${sharedY}`;
          }

        } else {
          // Para todas as outras conexões, garantir que getConnectorPath retorne caminho ortogonal
          
          // CRÍTICO: Detectar se há múltiplas saídas do mesmo card (avanço E retorno)
          // 🎯 FLATTENED VIEW: Usar visibleNodes para detectar targets
          const allReturnConnections = s.conexoes_saida.filter(conn => {
            const effectiveId = getEntryNodeId(conn.targetId);
            const targetCard = visibleNodes.find(tc => tc.id === effectiveId) || activeCards.find(tc => tc.id === conn.targetId);
            return targetCard && targetCard.posicao?.x < sourcePos.x - 10;
          });
          const allAdvanceConnections = s.conexoes_saida.filter(conn => {
            const effectiveId = getEntryNodeId(conn.targetId);
            const targetCard = visibleNodes.find(tc => tc.id === effectiveId) || activeCards.find(tc => tc.id === conn.targetId);
            return targetCard && targetCard.posicao?.x >= sourcePos.x - 10;
          });
          
          // Se há avanço E retorno, aplicar offset HORIZONTAL para separação visual
          // Setas saem JUNTAS, mas se separam pelo ponto onde viram
          let horizontalOffset = 0;
          if (allReturnConnections.length > 0 && allAdvanceConnections.length > 0) {
            const advanceIndex = allAdvanceConnections.findIndex(conn => conn.id === c.id);
            // CRÍTICO: Setas de avanço descem ANTES (offset menor)
            // Isso evita que encontrem outras setas
            horizontalOffset = 5; // Pequeno offset - desce logo após sair do card
          }
          
          // Ajustar sx com o offset horizontal (ponto onde a seta vira)
          if (horizontalOffset > 0) {
            // Criar path customizado com o offset no primeiro cotovelo
            const turnX = sx + horizontalOffset;
            d = `M ${sx} ${sy} L ${turnX} ${sy} L ${turnX} ${ty} L ${tx} ${ty}`;
          } else {
            d = getConnectorPath(sx, sy, tx, ty, c.sourceHandle, undefined, obstacles);
          }
          
          // Garantir que o caminho seja sempre ortogonal (sem diagonais)
          const segments = parsePathToSegments(d);
          if (segments.length > 0) {
            let normalizedPath = `M ${segments[0].x1} ${segments[0].y1}`;
            segments.forEach(seg => {
              normalizedPath += ` L ${seg.x2} ${seg.y2}`;
            });
            d = normalizedPath;
          }
        }

        // 🎯 JUMPS MANUAIS: Aplicar semicírculos onde o usuário definiu
        if (!isMaterialFlowConnection && c.manualJumps && c.manualJumps.length > 0) {
          d = applyManualJumps(d, c.manualJumps);
        }

        let color = isReturn ? '#ef4444' : '#64748b';
        let width = 2.5;
        const isPushConnection = effectiveConnectionType === 'push';
        if (isMaterialConnection && !isReturn) { width = 8.5; }
        if (isPushConnection && !isReturn) { color = '#000000'; width = 43.5; }
        if (effectiveConnectionType === 'vsm_transport_delivery' && !isReturn) { color = '#0891b2'; width = 3; }
        if (highlightedPath?.includes(s.id) && highlightedPath?.includes(t.id)) { color = '#2563eb'; width = 4; }
        if (currentLayout === 'classic') { color = '#000000'; width = isPushConnection ? 43.5 : isMaterialConnection ? 12 : 2; }

        const marker = isMaterialFlowConnection && !isReturn
          ? (color === '#2563eb' ? "url(#arrowhead-blue)" : "url(#arrowhead)")
          : color === '#2563eb' ? "url(#arrowhead-blue)" : color === '#0891b2' ? "url(#arrowhead-cyan)" : (color === '#ef4444' ? (ty < sy ? "url(#arrowhead-red-up)" : "url(#arrowhead-red)") : (ty < sy ? "url(#arrowhead-up)" : "url(#arrowhead)"));
        
        // 🎯 ARROW EXPLICITA: apenas merge points usam geometria manual; material usa markerEnd.
        // A seta deve apontar na direção de chegada ao endpoint.
        let customArrow: { x: number; y: number; angle: number } | undefined;
        if (hasMergePoint) {
          // Calcular direção de chegada baseado no último segmento do path
          // Pegar o penúltimo e último ponto do path para determinar a direção
          const segments = d.match(/[ML]\s*([\d.-]+)\s+([\d.-]+)/g);
          if (segments && segments.length >= 2) {
            const lastMatch = segments[segments.length - 1].match(/([\d.-]+)\s+([\d.-]+)/);
            const prevMatch = segments[segments.length - 2].match(/([\d.-]+)\s+([\d.-]+)/);
            if (lastMatch && prevMatch) {
              const lastX = parseFloat(lastMatch[1]);
              const lastY = parseFloat(lastMatch[2]);
              const prevX = parseFloat(prevMatch[1]);
              const prevY = parseFloat(prevMatch[2]);
              
              // Calcular ângulo de chegada
              const dx = lastX - prevX;
              const dy = lastY - prevY;
              
              let angle = 0;
              if (Math.abs(dx) > Math.abs(dy)) {
                // Chegando horizontalmente
                angle = dx > 0 ? 0 : 180; // 0 = direita, 180 = esquerda
              } else {
                // Chegando verticalmente  
                angle = dy > 0 ? 90 : -90; // 90 = baixo, -90 = cima
              }
              
              customArrow = { x: tx, y: ty, angle };
            }
          }
        }
        
        // DEBUG: Log final antes de adicionar ao array (comentado)
        // if (isParallel) {
        //   console.log(`[PARALLEL EDGE DEBUG] 🎯 ADICIONANDO CAMINHO AO ARRAY:`, {
        //     connectionId: c.id,
        //     sourceId: s.id,
        //     targetId: t.id,
        //     path: d,
        //     isParallel: true
        //   });
        // }
        
        pathsWithoutJumps.push({ 
          id: c.id, 
          d, 
          color, 
          width, 
          marker, 
          sourceId: s.id, 
          targetId: t.id, 
          connectionId: c.id, 
          sourceHandle: sourceHandleForGeometry, 
          sourcePoint: { x: sx, y: sy }, 
          targetPoint: { x: tx, y: ty }, 
          isReturn, 
          manualJumps: c.manualJumps,
          arrow: customArrow, // Arrow customizada para merge points
          connectionType: effectiveConnectionType
        });
      });
    });

    const pathsForPostProcessing = pathsWithoutJumps;

    // ============================================================================
    // FASE DE PÓS-PROCESSAMENTO: TRONCO DE RETORNO ÚNICO (Edge Merging)
    // ============================================================================
    // Detectar conexões que vão para o mesmo destino com linhas verticais 
    // paralelas e criar um único 'Tronco Vertical' de subida
    // ============================================================================
    
    // Agrupar TODAS as conexões pelo targetId para detectar convergências
    const connectionsByTarget = new Map<string, ProcessedConnection[]>();
    
    pathsForPostProcessing.forEach(conn => {
      const existing = connectionsByTarget.get(conn.targetId) || [];
      existing.push(conn);
      connectionsByTarget.set(conn.targetId, existing);
    });
    
    // Processar cada grupo de conexões para o mesmo destino
    const processedPaths: ProcessedConnection[] = [];
    const returnMergeNodes: Array<{ x: number; y: number; pathId: string; isMergeNode: boolean; targetId: string; color: string }> = [];
    
    connectionsByTarget.forEach((group, targetId) => {
      // Verificar se há conexões que precisam de fusão:
      // - Conexões de retorno (isReturn) 
      // - OU conexões que têm uma linha vertical subindo para entrar no card
      
      // Detectar conexões com linha vertical de subida (entram por baixo/esquerda do card)
      const connectionsWithVerticalRise = group.filter(conn => {
        // Parse do path para verificar se tem linha vertical subindo
        const pathStr = conn.d || '';
        const sourceY = conn.sourcePoint?.y || 0;
        const targetY = conn.targetPoint?.y || 0;
        
        // Se o sourceY é maior que targetY, a linha sobe em algum momento
        const needsToRise = sourceY > targetY + 10;
        
        // Ou se é um retorno
        return conn.isReturn || needsToRise;
      });
      
      if (connectionsWithVerticalRise.length <= 1) {
        // Apenas uma ou nenhuma conexão com subida vertical - manter como está
        processedPaths.push(...group);
        return;
      }
      
      // MÚLTIPLAS CONEXÕES COM SUBIDA VERTICAL PARA O MESMO DESTINO
      // Implementar Tronco de Retorno Único
      
      const targetCard = visibleNodes.find(c => c.id === targetId) || activeCards.find(c => c.id === targetId);
      if (!targetCard || !targetCard.posicao) {
        processedPaths.push(...group);
        return;
      }
      
      // Separar conexões que participam do merge das que não participam
      const nonMergeConnections = group.filter(conn => !connectionsWithVerticalRise.includes(conn));
      processedPaths.push(...nonMergeConnections);
      
      // Calcular posição do tronco vertical (40px à esquerda do card destino)
      const isTargetDiamond = targetCard.tipo === 'decisao';
      const targetHeight = isTargetDiamond ? 160 : 180;
      const targetYOffset = isTargetDiamond ? 10 : 0;
      const targetCenterY = targetCard.posicao.y + targetYOffset + (targetHeight / 2);
      const trunkX = targetCard.posicao.x - RETURN_TRUNK_OFFSET;
      
      // Ordenar conexões por distância do source (mais distante primeiro)
      const sortedConnections = [...connectionsWithVerticalRise].sort((a, b) => {
        const distA = Math.abs((a.sourcePoint?.x || 0) - targetCard.posicao.x);
        const distB = Math.abs((b.sourcePoint?.x || 0) - targetCard.posicao.x);
        return distB - distA; // Mais distante primeiro
      });
      
      // A conexão mais próxima define o tronco principal
      const closestConnection = sortedConnections[sortedConnections.length - 1];
      const furthestConnection = sortedConnections[0];
      
      // PRESERVAÇÃO DE CORES:
      // O tronco (após junção) assume a cor da linha que entrou primeiro (mais próxima)
      const trunkColor = closestConnection.color;
      
      // Calcular Y do subsolo (abaixo de todos os cards)
      let subsoloY = Math.max(
        ...sortedConnections.map(c => c.sourcePoint?.y || 0),
        targetCenterY
      ) + 160 + 80; // Card height + post-it space
      
      // Verificar obstáculos para garantir que passamos abaixo de todos
      const allSourceIds = sortedConnections.map(c => c.sourceId);
      const relevantObstacles = visibleNodes.filter(card => 
        !allSourceIds.includes(card.id) && 
        card.id !== targetId && 
        card.status === 'ativo'
      );
      
      relevantObstacles.forEach(obs => {
        if (!obs.posicao) return;
        const obsBottom = obs.posicao.y + 160;
        const obsX = obs.posicao.x;
        if (obsX > trunkX && obsX < (furthestConnection.sourcePoint?.x || 0)) {
          subsoloY = Math.max(subsoloY, obsBottom + 100);
        }
      });
      
      // Processar cada conexão
      sortedConnections.forEach((conn, index) => {
        const isClosest = index === sortedConnections.length - 1;
        const sx = conn.sourcePoint?.x || 0;
        const sy = conn.sourcePoint?.y || 0;
        const isBottomHandle = conn.sourceHandle === 'bottom';
        
        if (isClosest) {
          // CONEXÃO MAIS PRÓXIMA: Define o tronco principal
          let path: string;
          
          if (isBottomHandle) {
            // Saída pelo BOTTOM: desce direto → horizontal até trunkX → sobe
            path = `M ${sx} ${sy} L ${sx} ${subsoloY} L ${trunkX} ${subsoloY} L ${trunkX} ${targetCenterY} L ${targetCard.posicao.x} ${targetCenterY}`;
          } else {
            // Saída pela DIREITA: sai → desce → horizontal até trunkX → sobe
            const rightX = sx + 20;
            path = `M ${sx} ${sy} L ${rightX} ${sy} L ${rightX} ${subsoloY} L ${trunkX} ${subsoloY} L ${trunkX} ${targetCenterY} L ${targetCard.posicao.x} ${targetCenterY}`;
          }
          
          processedPaths.push({
            ...conn,
            d: path,
            targetPoint: { x: targetCard.posicao.x, y: targetCenterY }
          });
        } else {
          // CONEXÕES MAIS DISTANTES: Fazem junção em T com o tronco
          let path: string;
          
          // Ponto de junção no tronco vertical (no subsolo)
          const junctionY = subsoloY;
          
          if (isBottomHandle) {
            // Saída pelo BOTTOM: desce direto → horizontal até trunkX (junção em T)
            path = `M ${sx} ${sy} L ${sx} ${junctionY} L ${trunkX} ${junctionY}`;
          } else {
            // Saída pela DIREITA: sai → desce → horizontal até trunkX (junção em T)
            const rightX = sx + 20;
            path = `M ${sx} ${sy} L ${rightX} ${sy} L ${rightX} ${junctionY} L ${trunkX} ${junctionY}`;
          }
          
          // Usar marker especial para indicar junção em T (preservar cor)
          const junctionMarker = conn.color === '#ef4444' ? "url(#arrowhead-red)" : "url(#arrowhead)";
          
          processedPaths.push({
            ...conn,
            d: path,
            marker: junctionMarker,
            targetPoint: { x: trunkX, y: junctionY },
            stopsAtConvergence: true // Marcar que para na junção
          });
          
          // Adicionar nó de merge no ponto de junção (preservar cor original)
          returnMergeNodes.push({
            x: trunkX,
            y: junctionY,
            pathId: `return-junction-${conn.id}`,
            isMergeNode: true,
            targetId: targetId,
            color: conn.color // Preservar cor original para indicar tipo de retorno
          });
        }
      });
    });
    
    // Usar os paths processados (que já incluem todas as conexões)
    const allProcessedPaths = processedPaths;

    const targetGroups = groupConnectionsByTarget(allProcessedPaths);
    const mergedPaths: ProcessedConnection[] = [];
    const mergeNodes: Array<{ x: number; y: number; pathId: string; isMergeNode: boolean; targetId: string; isTrunkNode?: boolean }> = [];
    
    // Adicionar nós de junção de retorno aos merge nodes
    returnMergeNodes.forEach(node => {
      mergeNodes.push({
        ...node,
        isTrunkNode: false
      });
    });

    targetGroups.forEach((group, targetId) => {
      // CRÍTICO: Separar conexões que saem por bottom das que saem por right
      // Conexões de bottom NÃO devem participar do sistema de merge horizontal
      // Elas devem ser tratadas como independentes
      const bottomConnections = group.filter(conn => conn.sourceHandle === 'bottom');
      const rightConnections = group.filter(conn => conn.sourceHandle !== 'bottom');
      
      // Adicionar conexões de bottom diretamente (sem merge)
      bottomConnections.forEach(conn => {
        mergedPaths.push(conn);
      });
      
      // Se não há conexões pela direita, não há nada mais a fazer
      if (rightConnections.length === 0) {
        return;
      }
      
      // Se há apenas 1 conexão pela direita, adicionar diretamente
      if (rightConnections.length === 1) { 
        mergedPaths.push(rightConnections[0]);
        return; 
      }
      
      const targetCard = visibleNodes.find(c => c.id === targetId) || activeCards.find(c => c.id === targetId);
      if (!targetCard) { 
        mergedPaths.push(...rightConnections);
        return; 
      }
      
      // CRÍTICO: Priorizar linha principal (não vermelha) como trunk quando há múltiplas linhas
      // Se houver linha principal e linha vermelha convergindo para o mesmo destino,
      // a linha vermelha deve se juntar à linha principal antes de entrar no card
      const mainFlowPaths = rightConnections.filter(conn => !conn.isReturn && conn.color !== '#ef4444');
      const returnFlowPaths = rightConnections.filter(conn => conn.isReturn || conn.color === '#ef4444');
      
      // Se há linha principal, ela é o trunk. Caso contrário, usar a mais curta
      const trunk = mainFlowPaths.length > 0 
        ? (identifyTrunkPath(mainFlowPaths) || mainFlowPaths[0])
        : (identifyTrunkPath(rightConnections) || rightConnections[0]);
      
      // CORREÇÃO: Calcular mergeNodeX dinamicamente no MEIO do espaço entre cards
      // Em vez de usar posição fixa (targetCard.x - 60), calcular baseado nas origens
      
      // 1. Encontrar todos os cards de origem deste grupo (apenas conexões pela direita)
      // 🎯 FLATTENED VIEW: Usar visibleNodes para encontrar source cards
      const sourceCards = rightConnections.map(conn => visibleNodes.find(c => c.id === conn.sourceId) || activeCards.find(c => c.id === conn.sourceId)).filter(c => c !== undefined) as CardData[];
      
      // 2. Encontrar o card de origem mais à DIREITA (mais próximo do destino)
      let rightmostSourceX = 0;
      sourceCards.forEach(sourceCard => {
        const isSourceDiamond = sourceCard.tipo === 'decisao';
        const sourceWidth = isTimeScaleActive && sourceCard.calculatedWidth && sourceCard.calculatedWidth > 0 && !isSourceDiamond
          ? sourceCard.calculatedWidth
          : (isSourceDiamond ? 160 : 240);
        const sourceRightEdge = sourceCard.posicao?.x + sourceWidth;
        rightmostSourceX = Math.max(rightmostSourceX, sourceRightEdge);
      });
      
      // 3. Calcular o meio do espaço entre o card mais à direita e o destino
      const isTargetDiamond = targetCard.tipo === 'decisao';
      const targetWidth = isTimeScaleActive && targetCard.calculatedWidth && targetCard.calculatedWidth > 0 && !isTargetDiamond
        ? targetCard.calculatedWidth
        : (isTargetDiamond ? 160 : 240);
      const targetLeftEdge = targetCard.posicao?.x;
      
      // CRÍTICO: Calcular altura do card destino corretamente
      // Cards de decisão (losango) têm altura 160 + offset de 10px, cards normais têm altura 180
      const targetHeight = isTargetDiamond ? 160 : 180;
      const targetYOffset = isTargetDiamond ? 10 : 0;
      const targetCenterY = targetCard.posicao?.y + targetYOffset + (targetHeight / 2);
      
      const gap = targetLeftEdge - rightmostSourceX;
      
      // 4. Colocar o barramento vertical no MEIO do vão (com margem mínima de 40px antes do target)
      const mergeNodeX = gap > 80 
        ? rightmostSourceX + (gap / 2)  // Centro do vão
        : targetLeftEdge - 40;           // Fallback se cards estiverem muito próximos
      
      // CRÍTICO: Usar altura correta do card destino
      const mergeNodeY = targetCenterY;

      rightConnections.forEach(conn => {
        const isTrunk = conn.id === trunk.id;
        const convergence = isTrunk ? null : findConvergencePoint(conn.d, trunk.d);
        let edgePath = convergence ? truncatePathAtPoint(conn.d, convergence) : conn.d;
        
        // Garantir que o caminho truncado seja sempre ortogonal
        const segments = parsePathToSegments(edgePath);
        if (segments.length > 0) {
          let normalizedPath = `M ${segments[0].x1} ${segments[0].y1}`;
          segments.forEach(seg => {
            normalizedPath += ` L ${seg.x2} ${seg.y2}`;
          });
          edgePath = normalizedPath;
        }
        
        // Marcar se esta linha para na convergência (não é trunk e tem convergência)
        // CRÍTICO: Linhas vermelhas que convergem para linha principal devem parar na convergência
        const stopsAtConvergence = !isTrunk && convergence !== null;
        mergedPaths.push({ ...conn, d: edgePath, targetPoint: convergence || { x: mergeNodeX, y: mergeNodeY }, isReturn: conn.isReturn, stopsAtConvergence });
        // NÃO adicionar merge node aqui - será adicionado uma vez no barramento vertical abaixo
      });

      // Adicionar merge-edge com todas as propriedades necessárias
      // CRÍTICO: Usar targetCenterY em vez de valor fixo de 80
      const mergeEdge: ProcessedConnection = {
        id: `merge-edge-${targetId}`,
        d: getConnectorPath(mergeNodeX, mergeNodeY, targetCard.posicao?.x, targetCenterY, 'right'),
        color: trunk.color,
        width: trunk.width,
        marker: trunk.marker,
        sourceId: `merge-node-${targetId}`,
        targetId: targetId,
        connectionId: `merge-edge-${targetId}`,
        sourceHandle: 'right',
        sourcePoint: { x: mergeNodeX, y: mergeNodeY },
        targetPoint: { x: targetCard.posicao?.x, y: targetCenterY },
        isReturn: trunk.isReturn
      };
      mergedPaths.push(mergeEdge);
      // Adicionar apenas UM merge node no ponto do barramento vertical (onde a linha trunk continua)
      // Este é o ponto onde TODAS as linhas secundárias convergem
      mergeNodes.push({ x: mergeNodeX, y: mergeNodeY, pathId: `merge-node-${targetId}`, isMergeNode: true, targetId, isTrunkNode: true });
    });

    // Aplicar jumps a todos os paths após o merge, considerando todos os outros paths
    // IMPORTANTE: Não aplicar jumps em pontos de merge (convergência)
    const mergePoints = new Set<string>();
    mergeNodes.forEach(node => {
      // Adicionar todos os pontos de convergência (tanto os finais quanto os intermediários)
      mergePoints.add(`${Math.round(node.x)},${Math.round(node.y)}`);
    });
    
    // Também adicionar pontos de convergência das linhas secundárias
    mergedPaths.forEach(conn => {
      if (conn.targetPoint) {
        mergePoints.add(`${Math.round(conn.targetPoint.x)},${Math.round(conn.targetPoint.y)}`);
      }
    });
    
    // Separar linhas principais e linhas vermelhas
    const mainFlowPaths = mergedPaths.filter(p => !p.isReturn && p.color !== '#ef4444');
    // BOTTOM-SOURCED INFO CONNECTIONS (PCPM → processo): NÃO devem entrar no
    // pipeline de jumps/convergência de linhas de retorno horizontais. São setas
    // verticais independentes que descem do PCPM e não compartilham trilhos.
    const bottomInfoPaths = mergedPaths.filter(p => p.sourceHandle === 'bottom' && p.connectionType === 'vsm_info');
    const returnPaths = mergedPaths.filter(p => (p.isReturn || p.color === '#ef4444') && !(p.sourceHandle === 'bottom' && p.connectionType === 'vsm_info'));
    
    // PASSO 1: Aplicar jumps nas linhas vermelhas ANTES de qualquer processamento
    // CRÍTICO: Apenas a linha renderizada DEPOIS faz jump sobre a linha renderizada ANTES
    // Regra baseada na ordem de renderização (índice no array)
    
    // Criar mapa de índices originais para determinar ordem de renderização
    const returnPathIndices = new Map<string, number>();
    returnPaths.forEach((p, idx) => {
      returnPathIndices.set(p.id, idx);
    });
    
    // Agora aplicar jumps apenas nas linhas que devem fazer jump (renderizadas depois)
    const returnPathsWithJumps = returnPaths.map((returnPath, idx) => {
      // Coletar paths sobre os quais esta linha deve fazer jump
      const pathsToJumpOver: string[] = [];
      
      returnPaths.forEach((otherPath, otherIdx) => {
        if (otherIdx === idx) return;
        
        const intersections = detectIntersections(returnPath.d, otherPath.d);
        const segments = parsePathToSegments(returnPath.d);
        const otherSegments = parsePathToSegments(otherPath.d);
        
        intersections.forEach(inter => {
          // Filtrar pontos de merge
          if (mergePoints) {
            const interKey = `${Math.round(inter.x)},${Math.round(inter.y)}`;
            if (mergePoints.has(interKey)) return;
          }
          
          const seg = segments[inter.segmentIndexA];
          const otherSeg = otherSegments[inter.segmentIndexB];
          
          if (seg && otherSeg) {
            const isPerpendicular = 
              (seg.isHorizontal && !otherSeg.isHorizontal) || 
              (!seg.isHorizontal && otherSeg.isHorizontal);
            
            if (isPerpendicular) {
              // REGRA: Apenas a linha renderizada DEPOIS (índice maior) faz jump sobre a renderizada ANTES (índice menor)
              // A linha renderizada primeiro (índice menor) NÃO faz jump
              const thisIdx = returnPathIndices.get(returnPath.id) ?? idx;
              const otherIdxOriginal = returnPathIndices.get(otherPath.id) ?? otherIdx;
              
              // Se esta linha foi renderizada DEPOIS da outra, ela deve fazer jump sobre a outra
              if (thisIdx > otherIdxOriginal) {
                if (!pathsToJumpOver.includes(otherPath.d)) {
                  pathsToJumpOver.push(otherPath.d);
                }
              }
            }
          }
        });
      });
      
      // Aplicar jumps apenas se esta linha deve fazer jump sobre algum path
      if (pathsToJumpOver.length > 0) {
        const pathWithJumps = addLineJumps(returnPath.d, pathsToJumpOver, 5, mergePoints);
        return { ...returnPath, d: pathWithJumps };
      }
      
      return returnPath;
    });
    
    // PASSO 2: Para cada linha vermelha, verificar se cruza com linha principal que vai para o mesmo destino
    // Se cruzar, fazer convergir para a linha principal antes de entrar no card
    const returnPathsWithMainConvergence: ProcessedConnection[] = [];
    
    // Encontrar o índice original de cada linha vermelha para determinar ordem de renderização
    const returnPathOriginalIndices = new Map<string, number>();
    returnPaths.forEach((p, idx) => {
      returnPathOriginalIndices.set(p.id, idx);
    });
    
    returnPathsWithJumps.forEach((returnPath) => {
      // Encontrar linha principal que vai para o mesmo destino
      const sameTargetMainFlow = mainFlowPaths.find(mainPath => {
        const returnSegments = parsePathToSegments(returnPath.d);
        const mainSegments = parsePathToSegments(mainPath.d);
        
        if (returnSegments.length === 0 || mainSegments.length === 0) return false;
        
        const returnLast = returnSegments[returnSegments.length - 1];
        const mainLast = mainSegments[mainSegments.length - 1];
        const distance = Math.sqrt(
          Math.pow(returnLast.x2 - mainLast.x2, 2) + 
          Math.pow(returnLast.y2 - mainLast.y2, 2)
        );
        
        if (distance < 200) {
          const intersections = detectIntersections(returnPath.d, mainPath.d);
          return intersections.length > 0;
        }
        
        return false;
      });
      
      // CRÍTICO: Verificar se há outra linha vermelha que vai para o MESMO destino (mesmo targetId)
      const thisOriginalIdx = returnPathOriginalIndices.get(returnPath.id) ?? -1;
      const otherReturnPathToSameTarget = returnPathsWithJumps.find((otherPath) => {
        if (otherPath.id === returnPath.id) return false;
        
        // CRÍTICO: Verificar se o targetId é o MESMO (não apenas proximidade)
        const hasSameTarget = returnPath.targetId === otherPath.targetId;
        
        return hasSameTarget;
      });
      
      if (sameTargetMainFlow) {
        // Encontrar ponto de convergência com a linha principal
        const convergence = findConvergencePoint(returnPath.d, sameTargetMainFlow.d);
        
        if (convergence) {
          // Se há outra linha vermelha convergindo para o mesmo destino,
          // verificar qual deve parar na convergência entre elas ANTES de processar convergência com linha principal
          if (otherReturnPathToSameTarget) {
            const otherOriginalIdx = returnPathOriginalIndices.get(otherReturnPathToSameTarget.id) ?? -1;
            
            // Verificar se há interseção entre as duas linhas vermelhas
            const redIntersections = detectIntersections(returnPath.d, otherReturnPathToSameTarget.d);
            if (redIntersections.length > 0) {
              const segments = parsePathToSegments(returnPath.d);
              const otherSegments = parsePathToSegments(otherReturnPathToSameTarget.d);
              
              for (const intersection of redIntersections) {
                const seg = segments[intersection.segmentIndexA];
                const otherSeg = otherSegments[intersection.segmentIndexB];
                
                if (seg && otherSeg) {
                  const isPerpendicular = 
                    (seg.isHorizontal && !otherSeg.isHorizontal) || 
                    (!seg.isHorizontal && otherSeg.isHorizontal);
                  
                  if (isPerpendicular) {
                    // REGRA: A linha com índice MAIOR (renderizada depois) para na convergência com a outra vermelha
                    // A linha com índice MENOR (renderizada primeiro) continua até a linha preta
                    const shouldThisLineStopAtRed = thisOriginalIdx > otherOriginalIdx;
                    
                    if (shouldThisLineStopAtRed) {
                      // Esta linha para na convergência com a outra linha vermelha
                      // NÃO continua até a linha preta
                      const truncatePoint = {
                        x: intersection.x,
                        y: intersection.y,
                        segmentIndex: intersection.segmentIndexA
                      };
                      const truncatedPath = truncatePathAtPoint(returnPath.d, truncatePoint);
                      returnPathsWithMainConvergence.push({
                        ...returnPath,
                        d: truncatedPath,
                        targetPoint: { x: intersection.x, y: intersection.y },
                        stopsAtConvergence: true
                      });
                      return; // Não processar convergência com linha principal - esta linha já parou
                    }
                    // Se esta linha deve continuar, processar convergência com linha principal normalmente abaixo
                    break;
                  }
                }
              }
            }
          }
          
          // Esta linha continua até a linha preta (primeira renderizada ou não há outra vermelha)
          // NÃO truncar o path - manter os jumps (arcos) intactos
          // Apenas marcar o ponto de convergência para inserir a seta
          returnPathsWithMainConvergence.push({
            ...returnPath,
            d: returnPath.d, // Manter o path completo com jumps preservados
            targetPoint: { x: convergence.x, y: convergence.y },
            stopsAtConvergence: true // Esta linha PARA na convergência com a linha principal
          });
        } else {
          returnPathsWithMainConvergence.push(returnPath);
        }
      } else {
        // NOVA ABORDAGEM: Verificar convergências entre linhas vermelhas usando análise de segmentos
        // Encontrar todas as linhas vermelhas anteriores (índice menor)
        const thisOriginalIdx = returnPaths.findIndex(pp => pp.id === returnPath.id);
        const otherReturnPaths = returnPathsWithJumps.filter((p) => {
          const otherOriginalIdx = returnPaths.findIndex(pp => pp.id === p.id);
          return p.id !== returnPath.id && otherOriginalIdx !== -1 && otherOriginalIdx < thisOriginalIdx;
        });
        
        let finalPath = returnPath.d;
        let convergencePoint: { x: number; y: number } | null = null;
        let stopsAtConvergence = false;
        
        // Analisar os segmentos da linha atual
        const currentSegments = parsePathToSegments(returnPath.d);
        if (currentSegments.length === 0) {
          returnPathsWithMainConvergence.push(returnPath);
          return;
        }
        
        // Encontrar TODOS os segmentos horizontais da linha atual (que vão para a esquerda)
        const currentHorizontalSegments = currentSegments.filter(seg => seg.isHorizontal);
        
        // Para cada linha vermelha anterior, verificar se cruza
        for (const otherPath of otherReturnPaths) {
          const otherSegments = parsePathToSegments(otherPath.d);
          if (otherSegments.length === 0) continue;
          
          // Encontrar o último segmento horizontal da outra linha (que vai para a esquerda)
          const otherHorizontalSegments = otherSegments.filter(seg => seg.isHorizontal);
          if (otherHorizontalSegments.length === 0) continue;
          
          // Pegar o último segmento horizontal da outra linha (mais próximo do destino)
          const otherLastHorizontal = otherHorizontalSegments[otherHorizontalSegments.length - 1];
          const otherHorizontalY = otherLastHorizontal.y1; // Y do segmento horizontal da outra linha
          const otherHorizontalX = Math.min(otherLastHorizontal.x1, otherLastHorizontal.x2); // X mais à esquerda (onde ela vai)
          
          // Verificar se algum segmento horizontal da linha atual está na mesma altura Y
          for (const currentHorizontal of currentHorizontalSegments) {
            const currentHorizontalY = currentHorizontal.y1;
            const currentHorizontalXStart = Math.min(currentHorizontal.x1, currentHorizontal.x2);
            const currentHorizontalXEnd = Math.max(currentHorizontal.x1, currentHorizontal.x2);
            
            // Se estão na mesma altura Y (com tolerância de 5px)
            if (Math.abs(currentHorizontalY - otherHorizontalY) < 5) {
              // Se a linha atual ainda não alcançou o X da outra linha, ela deve parar quando alcançar
              // Mas se já passou, não precisa parar (já convergiu)
              if (currentHorizontalXEnd > otherHorizontalX) {
                // A linha atual deve parar no X da outra linha
                convergencePoint = { x: otherHorizontalX, y: currentHorizontalY };
                
                // Truncar o path até o ponto de convergência
                const truncatePoint = {
                  x: convergencePoint.x,
                  y: convergencePoint.y,
                  segmentIndex: currentSegments.indexOf(currentHorizontal)
                };
                
                finalPath = truncatePathAtPoint(returnPath.d, truncatePoint);
                stopsAtConvergence = true;
                break; // PARAR na primeira linha vermelha encontrada
              }
            }
          }
          
          if (stopsAtConvergence) break; // Se já encontrou convergência, parar de procurar
        }
        
        returnPathsWithMainConvergence.push({
          ...returnPath,
          d: finalPath,
          targetPoint: convergencePoint || returnPath.targetPoint,
          stopsAtConvergence: stopsAtConvergence
        });
      }
    });
    
    // PASSO 3: Combinar todas as linhas processadas
    const allPathsWithJumps = [...mainFlowPaths, ...returnPathsWithMainConvergence, ...bottomInfoPaths];

    // CORREÇÃO ANTI-DUPLICAÇÃO: Filtrar conexões duplicadas antes de retornar
    const uniquePaths = getUniqueConnections(allPathsWithJumps);
    
    // DEBUG: Verificar se havia duplicatas
    const duplicates = findDuplicateConnections(allPathsWithJumps);

    return { paths: uniquePaths, mergeNodes: mergeNodes.filter(m => m.isMergeNode) };
  }, [activeCards, visibleNodes, expandedGroupsInfo, redirectMap, getEntryNodeId, highlightedPath, currentLayout, isTimeScaleActive]); // CRÍTICO: Incluir dependências de grupos expandidos

  const processedConnections = useMemo(() => {
    if (!connectionPaths?.paths) return [] as ProcessedConnection[];
    
    // 🛣️ CHANNEL ROUTER: Se ativo, as arrows já estão calculadas pelo router
    // Apenas retornar os paths diretamente
    if (USE_CHANNEL_ROUTER) {
      return connectionPaths.paths as ProcessedConnection[];
    }
    
    // Primeiro, identificar quais linhas principais têm linhas vermelhas parando na convergência NO MEIO do path
    // (não no final, pois se for no final, o merge-edge terá a seta)
    const mainPathsWithRedConvergenceMidPath = new Set<string>();
    connectionPaths.paths.forEach((redPath: ProcessedConnection) => {
      if (redPath.color === '#ef4444' && redPath.stopsAtConvergence && redPath.targetPoint) {
        // Encontrar linha principal que tem o mesmo targetPoint (convergência)
        connectionPaths.paths.forEach((mainPath: ProcessedConnection) => {
          if (mainPath.color !== '#ef4444' && mainPath.targetPoint && !mainPath.id?.startsWith('merge-edge-')) {
            const distance = Math.sqrt(
              Math.pow(mainPath.targetPoint.x - redPath.targetPoint.x, 2) +
              Math.pow(mainPath.targetPoint.y - redPath.targetPoint.y, 2)
            );
            if (distance < 5) {
              // Verificar se a convergência está NO MEIO do path (não no final)
              const commands = mainPath.d.match(/[ML] [-\d\.]+ [-\d\.]+/gi) || [];
              if (commands.length >= 2) {
                const last = commands[commands.length - 1].split(/\s+/);
                const lastX = parseFloat(last[1]);
                const lastY = parseFloat(last[2]);
                // Se o targetPoint está longe do último ponto (> 1px), é convergência no meio do path
                const isConvergenceMidPath = Math.abs(mainPath.targetPoint.x - lastX) > 1 || 
                                             Math.abs(mainPath.targetPoint.y - lastY) > 1;
                if (isConvergenceMidPath) {
                  mainPathsWithRedConvergenceMidPath.add(mainPath.id);
                }
              }
            }
          }
        });
      }
    });
    
    const getPathLength = (d: string): number => {
      const points = (d.match(/[ML]\s*[-\d.]+\s+[-\d.]+/gi) || [])
        .map(command => {
          const match = command.match(/([-\d.]+)\s+([-\d.]+)$/);
          return match ? { x: Number(match[1]), y: Number(match[2]) } : null;
        })
        .filter((point): point is { x: number; y: number } => !!point && Number.isFinite(point.x) && Number.isFinite(point.y));
      return points.slice(1).reduce((total, point, index) => {
        const previous = points[index];
        return total + Math.hypot(point.x - previous.x, point.y - previous.y);
      }, 0);
    };

    const isEntityOrPcpmPath = (conn: ProcessedConnection): boolean => {
      const sourceCard = visibleNodes.find(c => c.id === conn.sourceId) || activeCards.find(c => c.id === conn.sourceId);
      const targetCard = visibleNodes.find(c => c.id === conn.targetId) || activeCards.find(c => c.id === conn.targetId);
      const isEntityOrPcpm = (card?: CardData) => !!card && (
        ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '') ||
        ['pcpm', 'nivelamento', 'info_eletro'].includes((card as any).vsmIcon ?? '')
      );
      return isEntityOrPcpm(sourceCard) || isEntityOrPcpm(targetCard);
    };

    const visiblePaths = connectionPaths.paths.filter((p: ProcessedConnection) => {
      const isVsmDirectional = p.connectionType === 'vsm_info' || p.connectionType === 'vsm_transport' || p.connectionType === 'vsm_transport_delivery';
      return !(isVsmDirectional && isEntityOrPcpmPath(p) && getPathLength(p.d) < 90);
    });

    return visiblePaths.map((p: ProcessedConnection, idx: number): ProcessedConnection => {
      // PRIORIDADE 1: Se há um targetPoint (ponto de convergência) E a linha PARA, usar ele para a seta
      // CRÍTICO: Quando para na convergência, calcular APENAS a seta no ponto de convergência
      // NÃO calcular seta adicional no final do path
      if (p.targetPoint && p.targetPoint.x !== undefined && p.targetPoint.y !== undefined && p.stopsAtConvergence) {
        const commands = p.d.match(/[ML] [-\d\.]+ [-\d\.]+/gi) || [];
        if (commands.length >= 2) {
          // Usar o penúltimo e último ponto para calcular a direção do último segmento
          const lastCommand = commands[commands.length - 1];
          const prevCommand = commands[commands.length - 2];
          
          // Extrair coordenadas corretamente - pegar apenas os números
          const lastMatch = lastCommand.match(/([-\d.]+)\s+([-\d.]+)/);
          const prevMatch = prevCommand.match(/([-\d.]+)\s+([-\d.]+)/);
          
          if (lastMatch && prevMatch) {
            const lastX = parseFloat(lastMatch[1]);
            const lastY = parseFloat(lastMatch[2]);
            const prevX = parseFloat(prevMatch[1]);
            const prevY = parseFloat(prevMatch[2]);
            
            // Calcular ângulo do último segmento (do penúltimo ao último ponto)
            const dx = lastX - prevX;
            const dy = lastY - prevY;
            
            // CRÍTICO: Para linhas que sobem verticalmente, garantir ângulo de 90°
            // Para linhas que vão horizontalmente, garantir ângulo de 0° ou 180°
            const isVertical = Math.abs(dy) > Math.abs(dx);
            const isHorizontal = Math.abs(dx) > Math.abs(dy);
            
            // Se linha sobe (dy negativo e vertical), garantir -90° (para cima)
            let finalAngle = 0;
            if (isVertical && dy < 0) {
              finalAngle = -90; // Para cima
            } else if (isVertical && dy > 0) {
              finalAngle = 90; // Para baixo
            } else if (isHorizontal && dx > 0) {
              finalAngle = 0; // Para direita
            } else if (isHorizontal && dx < 0) {
              finalAngle = 180; // Para esquerda
            }
            
            return { 
              ...p, 
              arrow: { 
                x: p.targetPoint.x, 
                y: p.targetPoint.y, 
                angle: finalAngle 
              } 
            };
          }
        } else if (commands.length === 1) {
          // Se só tem um ponto, usar o targetPoint como referência
          const lastCommand = commands[0];
          const lastCoords = lastCommand.split(/\s+/);
          const lastX = parseFloat(lastCoords[1]);
          const lastY = parseFloat(lastCoords[2]);
          
          // Se o último ponto é muito próximo do targetPoint, usar um ângulo padrão (para cima)
          const dx = p.targetPoint.x - lastX;
          const dy = p.targetPoint.y - lastY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // CRÍTICO: Se está parando na convergência, SEMPRE calcular seta
          if (p.stopsAtConvergence) {
            let angle = 90; // Padrão: para cima
            if (distance > 0.1) {
              angle = Math.atan2(dy, dx) * (180 / Math.PI);
            }
            return { 
              ...p, 
              arrow: { 
                x: p.targetPoint.x, 
                y: p.targetPoint.y, 
                angle 
              } 
            };
          }
          
          // Para linhas vermelhas normais (não convergindo), remover setas horizontais
          if (p.color === '#ef4444') {
            const isHorizontal = Math.abs(dx) > Math.abs(dy);
            if (isHorizontal) {
              return { ...p, arrow: undefined };
            }
          }
          
          let angle = 90; // Padrão: para cima
          if (distance > 0.1) {
            angle = Math.atan2(dy, dx) * (180 / Math.PI);
          }
          
          return { 
            ...p, 
            arrow: { 
              x: p.targetPoint.x, 
              y: p.targetPoint.y, 
              angle 
            } 
          };
        }
      }
      
      // PRIORIDADE 2: Se há um targetPoint mas a linha CONTINUA através da convergência (horizontal)
      // NÃO calcular seta no ponto de convergência nem no final se for horizontal (esquerda→direita)
      if (p.targetPoint && p.targetPoint.x !== undefined && p.targetPoint.y !== undefined && !p.stopsAtConvergence && p.color === '#ef4444') {
        // Verificar o segmento que passa pelo ponto de convergência
        const commands = p.d.match(/[ML] [-\d\.]+ [-\d\.]+/gi) || [];
        if (commands.length >= 2) {
          // Encontrar o segmento que contém o targetPoint
          for (let i = 0; i < commands.length - 1; i++) {
            const seg1 = commands[i].split(/\s+/);
            const seg2 = commands[i + 1].split(/\s+/);
            const x1 = parseFloat(seg1[seg1.length - 2]);
            const y1 = parseFloat(seg1[seg1.length - 1]);
            const x2 = parseFloat(seg2[seg2.length - 2]);
            const y2 = parseFloat(seg2[seg2.length - 1]);
            
            // Verificar se o targetPoint está neste segmento
            const minX = Math.min(x1, x2);
            const maxX = Math.max(x1, x2);
            const minY = Math.min(y1, y2);
            const maxY = Math.max(y1, y2);
            
            if (p.targetPoint.x >= minX - 1 && p.targetPoint.x <= maxX + 1 &&
                p.targetPoint.y >= minY - 1 && p.targetPoint.y <= maxY + 1) {
              // Este é o segmento que passa pelo ponto de convergência
              const dx = x2 - x1;
              const dy = y2 - y1;
              const isHorizontal = Math.abs(dx) > Math.abs(dy);
              const isLeftToRight = dx > 0;
              
              // Se o segmento que passa pela convergência é horizontal (esquerda→direita), NÃO calcular seta
              if (isHorizontal && isLeftToRight) {
                return { ...p, arrow: undefined };
              }
              break;
            }
          }
        }
      }
      
      // PRIORIDADE 3: Para linhas vermelhas (retornos), verificar se o último segmento é horizontal
      // Remover setas horizontais (tanto esquerda→direita quanto direita→esquerda)
      if (p.color === '#ef4444') {
        const commands = p.d.match(/[ML] [-\d\.]+ [-\d\.]+/gi) || [];
        if (commands.length >= 2) {
          const lastCommand = commands[commands.length - 1];
          const prevCommand = commands[commands.length - 2];
          
          // Extrair coordenadas corretamente
          const lastMatch = lastCommand.match(/([-\d.]+)\s+([-\d.]+)/);
          const prevMatch = prevCommand.match(/([-\d.]+)\s+([-\d.]+)/);
          
          if (lastMatch && prevMatch) {
            const lastX = parseFloat(lastMatch[1]);
            const lastY = parseFloat(lastMatch[2]);
            const prevX = parseFloat(prevMatch[1]);
            const prevY = parseFloat(prevMatch[2]);
            
            // Calcular diferença do último segmento
            const dx = lastX - prevX;
            const dy = lastY - prevY;
            
            // Se o movimento horizontal é maior que o vertical, NÃO calcular seta
            // Isso remove setas horizontais em qualquer direção (esquerda→direita e direita→esquerda)
            const isHorizontal = Math.abs(dx) > Math.abs(dy);
            
            if (isHorizontal) {
              return { ...p, arrow: undefined };
            }
            
            // Se é vertical, calcular seta com ângulo correto
            const isVertical = Math.abs(dy) > Math.abs(dx);
            if (isVertical) {
              let angle = -90; // Para cima (padrão)
              if (dy > 0) angle = 90; // Para baixo
              
              return { 
                ...p, 
                arrow: { 
                  x: lastX, 
                  y: lastY, 
                  angle 
                } 
              };
            }
          }
        }
      }
      
      // CRÍTICO: merge-edge NÃO deve ter seta própria
      // A seta já está sendo renderizada pela linha principal que chega ao merge node
      // Renderizar seta no merge-edge causaria duplicação
      if (p.id && p.id.startsWith('merge-edge-')) {
        return { ...p, arrow: undefined };
      }

      if (p.connectionType === 'material' || p.connectionType === 'push') {
        return { ...p, arrow: undefined };
      }
      
      // CRÍTICO: Se esta linha principal tem uma linha vermelha parando na convergência NO MEIO do path,
      // NÃO calcular seta no ponto de convergência - apenas o merge-edge terá seta
      // Se a convergência está no final do path (linha vai até merge node), o merge-edge terá a seta
      if (p.color !== '#ef4444' && !p.id?.startsWith('merge-edge-') && mainPathsWithRedConvergenceMidPath.has(p.id)) {
        // Esta linha principal tem uma linha vermelha parando na convergência NO MEIO do path
        // Não calcular seta aqui - apenas o merge-edge terá seta no final
        return { ...p, arrow: undefined };
      }
      
      // CRÍTICO: Se há convergência com linha vermelha (targetPoint definido mas não stopsAtConvergence),
      // e esta NÃO é uma linha vermelha, então é uma linha principal que continua até o merge node
      // Se a convergência está NO FINAL do path (linha vai até merge node), calcular seta normalmente
      // Se a convergência está NO MEIO do path, não calcular seta aqui (já tratado acima)
      // Esta verificação é redundante com a anterior, mas mantida para clareza
      
      // Fallback: calcular seta no final do path (comportamento original)
      // Mas apenas se NÃO parou na convergência (já tratado acima)
      const commands = p.d.match(/[ML] [-\d\.]+ [-\d\.]+/gi) || [];
      if (commands.length < 2) return p;
      const last = commands[commands.length - 1].split(/\s+/);
      const prev = commands[commands.length - 2].split(/\s+/);
      const ex = parseFloat(last[1]), ey = parseFloat(last[2]);
      const px = parseFloat(prev[prev.length - 2]), py = parseFloat(prev[prev.length - 1]);
      
      return { ...p, arrow: { x: ex, y: ey, angle: Math.atan2(ey - py, ex - px) * (180 / Math.PI) } };
    });
  }, [connectionPaths]);

  return { connectionPaths, processedConnections, mergeNodes: connectionPaths.mergeNodes };
};
